<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import KeyCap from './KeyCap.vue';
import type { HudState, KillFeedEntry } from '@/game/arena-scene';
import {
  CTF_CAPTURES_TO_WIN,
  DEATHMATCH_KILL_TARGET,
  CONVOY_ESCORT_RADIUS,
  TEAM_ORDERS_ENABLED,
  WAVE_COUNT,
  convoyHalted,
  missionKindFor,
  type MatchMode,
  type MissionKind,
  type SortieSettlement,
} from '@shared/protocol';
import {
  convoyStatusLabel,
  enemiesLeft,
  flagReturnSeconds,
  formatClock,
  legOnScreen,
  secondsUntil,
  waveOnScreen,
} from '@/game/mission-hud';
import SettingsPanel from './SettingsPanel.vue';
import RadarScope from './RadarScope.vue';
import GearIcon from './GearIcon.vue';
import TouchControls from './TouchControls.vue';
import TitleBadge from './TitleBadge.vue';
import BattleSummary from './BattleSummary.vue';
import ResultFuelButton from './ResultFuelButton.vue';

const props = defineProps<{
  hud: HudState;
  nickname: string;
  /**
   * The arena this is, as the server named it.
   *
   * With several arenas running, "which battle am I in" is a real question — and
   * a join to an arena that had just been reaped lands in quick match instead,
   * which the player has no other way to notice. Null until `joined` arrives.
   */
  arenaName?: string | null;
  /**
   * What the sortie paid, once the player has asked to leave and the server has
   * banked it. Null the whole time a battle is being played.
   *
   * Every figure in it is the server's — see `SortieSettlement`. This component
   * renders them and computes nothing, which is why there is no "35% of what?"
   * arithmetic anywhere in this file: the credits *are* the reduced credits.
   */
  settlement?: SortieSettlement | null;
}>();

/**
 * The clear ground between the two thumb controls, published to CSS.
 *
 * The health bar has to live between the thumbs, and the *exact* gap is already
 * known — `touch-layout.ts` placed both controls and the input source publishes
 * them, so this reads the same circles the hit-test uses rather than guessing with
 * a `calc(100vw - 26rem)`. That matters because the controls are sized from the
 * viewport's shorter edge and then clamped, so the gap is not a fixed fraction of
 * the width: it is 435px on an iPhone SE landscape and 866px on an iPad.
 *
 * Centred on the **gap**, not on the screen. At 667×375 the gap runs x 134–569,
 * whose midpoint is 351 against a screen centre of 333 — so a screen-centred bar
 * of the full gap width would overhang the stick by 18px.
 */
const compactVars = computed((): Record<string, string> => {
  const t = props.hud.touch;
  if (!t) return {};
  const left = t.layout.stick.cx + t.layout.stick.radius;
  const right = t.layout.fire.cx - t.layout.fire.radius;
  return {
    '--thumb-gap': `${Math.round(Math.max(0, right - left))}px`,
    '--thumb-gap-centre': `${Math.round((left + right) / 2)}px`,
    '--thumb-band': `${Math.round(t.layout.stick.radius * 2)}px`,
  };
});

/**
 * Whether the full roster is open over the battle.
 *
 * Collapsed by default on a touch build, because the scoreboard measures 227×264
 * — **70% of the height** of an iPhone SE in landscape — and the movement stick
 * clips its bottom rows by 4,354px². What replaces it always-on is the score
 * alone, which is the part anybody reads mid-fight; *who is alive* and *who is
 * topping the board* are questions asked between engagements, and one tap is a
 * fair price for them.
 *
 * It deliberately does **not** suspend the input source. The battle is still
 * running and the player can still be shot, so freezing the tank because a
 * scoreboard is open would be the worse trade — the overlay closes on a tap
 * anywhere instead.
 */
const rosterOpen = ref(false);

const emit = defineEmits<{
  /** Go: tear the socket down and return to the garage. */
  leave: [];
  /**
   * *Ask* to leave a battle that is still running.
   *
   * Separate from `leave` because leaving mid-battle costs the whole payout — a
   * sortie is paid for finishing it — and a player is owed that answer before the
   * screen changes under them. The parent settles with the server and hands it
   * back as `settlement`; this component then shows it and emits `leave` when the
   * player is done reading. A decided battle skips all of it and emits `leave`
   * directly, because there is nothing left to forfeit.
   */
  requestLeave: [];
  menu: [open: boolean];
}>();

/**
 * Whether leaving right now would cost anything.
 *
 * `hud.outcome` is set the moment a battle is decided, and the payout clock stops
 * at that same tick, so past it leaving is free and immediate. Before it, leaving
 * forfeits the entire payout — which is emphatically the one case worth
 * interrupting for.
 */
const leavingCosts = computed(() => !props.hud.outcome);

/**
 * Both exits go through here, so the two doors cannot drift apart.
 *
 * A dynamic `$emit(cond ? a : b)` in the template does not typecheck against a
 * typed `defineEmits` — and a named function is the better shape anyway, since
 * *which* event a Leave button raises is a decision rather than markup.
 */
function askToLeave(): void {
  if (leavingCosts.value) emit('requestLeave');
  else emit('leave');
}

/*
 * There is deliberately no *Restart* and no *Skip* here any more.
 *
 * Both were client-side: the browser owned the progress, so it could rewind or
 * step over it. The tutorial is a mission now and the server owns the script, so
 * a skip would be a new command on a wire whose whole discipline is that a
 * client asserts nothing — for a room that is not skippable in the first place.
 *
 * Nothing was lost. **Restart is leaving and starting again**, because
 * `ArenaService.tutorial` rebuilds an empty room rather than resuming one. And
 * **skip existed because two of the four old lessons were not in the player's
 * hands** — a kill needed the enemy to cooperate and a crate fell when the
 * arena's timer said so — whereas every drill here is a control the player
 * holds, and the mission has no losing condition to be stuck on: a destroyed
 * learner is put back on their feet with the drill's progress intact.
 */

/**
 * The escape menu.
 *
 * It is a *menu*, not a pause. The arena is authoritative and shared, so there
 * is nothing here that could stop it — bots keep driving and shells keep flying
 * whatever this player is reading. What opening it does do is take the controls
 * away from the tank, which the scene arranges through `InputState`; the panel
 * says so out loud rather than letting a player discover it by being shot.
 */
const menuOpen = ref(false);

/**
 * Escape toggles it, from a window listener.
 *
 * On `window` rather than on the overlay because the menu has to be reachable
 * while the canvas has focus, which is all of the time, and because an overlay
 * that listened for its own dismissal would stop hearing it the moment focus
 * landed on one of its own controls. Nothing between here and the window calls
 * `stopPropagation`, so the key that opens the menu is the same key that closes
 * it however deep in the panel the player has clicked.
 *
 * The browser drops pointer lock on the same keypress. That is convenient
 * rather than a problem — the cursor is exactly what a menu needs — and
 * `setSuspended` asks for the lock back on the way out.
 */
const onKeyDown = (e: KeyboardEvent): void => {
  if (e.code !== 'Escape' || e.repeat) return;
  e.preventDefault();
  menuOpen.value = !menuOpen.value;
};

/**
 * The three result paintings, so the debrief is whole the moment it appears.
 *
 * Stated here as well as in the stylesheet, which draws them — the CSS is the
 * source of truth and this is an optimisation whose failure would be invisible,
 * so `result-art.test.ts` cross-checks the two lists against each other and
 * against the files on disk rather than leaving them to drift.
 *
 * **Not gated on device or connection, unlike `App.vue`'s engine warm-up**, and
 * the difference is that this is not speculative. That one pulls megabytes for a
 * visitor who may only be reading the landing page; by the time this component
 * exists the player is in a battle that has already fetched the engine, the
 * map's props and two tank GLBs, and one of these three paintings is *certain*
 * to be shown when it ends. 235 KB fetched early rather than late — less than
 * half the music bed this game already ships unconditionally.
 *
 * On idle because it must never compete with the arena's own load: this mounts
 * beside the loading screen, so an eager fetch would be racing the terrain the
 * player is waiting for.
 */
const RESULT_ART = [
  '/assets/result/victory.webp',
  '/assets/result/loss.webp',
  '/assets/result/left.webp',
];

function warmResultArt(): void {
  const warm = () => {
    for (const src of RESULT_ART) {
      // Decoded into the image cache and dropped; the stylesheet asks for the
      // same URL later and gets it warm. Failure is silence — a painting that
      // does not arrive leaves the flat scrim the screen had before it.
      const img = new Image();
      img.src = src;
    }
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  // Safari has no `requestIdleCallback`, exactly as `warmArenaScene` notes; a
  // timeout is the same intent one guarantee weaker, and a battle lasts minutes.
  if (idle) idle(warm);
  else window.setTimeout(warm, 3000);
}

/**
 * Every result now gets a looping video over its painting — `victory.*` on a
 * win, `loss.*` on a defeat or a draw (the two outcomes that already shared
 * one picture, per `.result`'s own comment, share the video too). Faded in
 * once frames are actually rendering (`@playing`, not `@canplay`: buffered
 * enough to be *allowed* to play is not the same as anything being on screen
 * yet). `.result`'s own still background stays under it unconditionally, so
 * a slow network, a 404, a codec the browser lacks, or `prefers-reduced-
 * motion` all land on the same painting this screen always showed — the
 * video is a pure addition. The abandoned screen (`left.webp`) keeps no
 * video at all; nobody asked for one and there is no footage for it.
 */
const resultVideoReady = ref(false);
const resultReducedMotion =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
const resultVideoSources = computed(() =>
  props.hud.won
    ? { webm: '/assets/result/victory.webm', mp4: '/assets/result/victory.mp4' }
    : { webm: '/assets/result/loss.webm', mp4: '/assets/result/loss.mp4' },
);

onMounted(() => {
  window.addEventListener('keydown', onKeyDown);
  warmResultArt();
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown);
  // Leaving with the menu open must not strand a suspended input source: the
  // scene outlives this component's render, and `App.vue` disposes it after.
  if (menuOpen.value) emit('menu', false);
});

watch(menuOpen, (open) => emit('menu', open));

/**
 * Both exits are handled the same way here; the difference lives in the browser.
 *
 * Leaving by the Resume button restores pointer lock, because the click is a
 * user gesture. Leaving by Escape does not — Chrome refuses a request made
 * during its own exit and blocks re-locking for about a second afterwards.
 *
 * **That used to strand the player**, because lock was only asked for again on
 * a *click*, which is also a shot. Unlocked, aiming is bounded by the window:
 * turn far enough one way and the real cursor pins against the edge of the
 * screen and the gun stops turning, with nothing on screen to say why. It was
 * reported as "the camera refuses to go further left". `InputState.onKeyDown`
 * now asks for the lock back too, so the first movement key after the menu
 * closes restores it.
 */
function closeMenu(): void {
  menuOpen.value = false;
}

/**
 * The combat fields on `HudState` are optional — `App.vue` builds the initial
 * value as a literal and is owned elsewhere, so it cannot be made to fill them
 * in. Everything downstream reads this normalised view instead of scattering
 * `?? 0` through the template.
 */
const combat = computed(() => ({
  health: props.hud.health ?? 0,
  maxHealth: props.hud.maxHealth || 100,
  alive: props.hud.alive ?? true,
  respawnInSeconds: props.hud.respawnInSeconds ?? 0,
  reloadProgress: props.hud.reloadProgress ?? 1,
  chargeProgress: props.hud.chargeProgress ?? 0,
  kills: props.hud.kills ?? 0,
  deaths: props.hud.deaths ?? 0,
  killFeed: props.hud.killFeed ?? [],
  buffs: props.hud.buffs ?? [],
  nameplates: props.hud.nameplates ?? [],
  gunMarker: props.hud.gunMarker ?? null,
  aimReticle: props.hud.aimReticle ?? null,
  // Null rather than absent, like the gun marker beside it: the template asks
  // `v-if` on it, and "no objective" is the answer in every mode but one.
  objective: props.hud.objective ?? null,
  damageFlash: props.hud.damageFlash ?? 0,
  hitMarkers: props.hud.hitMarkers ?? [],
  // Null rather than absent, like the objective beside it: the template asks
  // `v-if` on it, and "no banner due" is the answer nearly always.
  spawnZoneWarning: props.hud.spawnZoneWarning ?? null,
}));

const healthFraction = computed(() =>
  Math.max(0, Math.min(1, combat.value.health / combat.value.maxHealth)),
);

/** Green down to a third, then amber, then red. Colour is the first warning. */
function healthColour(fraction: number): string {
  if (fraction > 0.6) return '#4CAF6D';
  if (fraction > 0.3) return '#D9A22B';
  return '#D9483B';
}

/**
 * A floating bar's colour.
 *
 * An enemy's is red whatever its health, because "how hurt is that tank" is a
 * second question and "is it going to shoot me" is the first. An ally's runs the
 * usual green-amber-red scale, since for a team-mate the health *is* the news.
 */
function plateColour(plate: { health: number; maxHealth: number; friendly?: boolean }): string {
  if (!plate.friendly) return '#D9483B';
  return healthColour(plate.health / (plate.maxHealth || 1));
}

/**
 * A kill-feed name's colour, from which side it was on.
 *
 * The same two hexes the nameplate over a tank paints with (`.plate-name` /
 * `.nameplate.hostile .plate-name`, below), so a name read here and the same
 * name read over a tank never disagree about which side it means. `undefined`
 * for the feed's non-kill lines — an airdrop claim, a round result, a mission
 * cue — which have no side to report; binding `undefined` to `:style="{
 * color }"` leaves the attribute unset rather than writing `color: undefined`,
 * so those lines fall through to `.killer`/`.victim`'s plain white/steel rule.
 */
function sideColour(friendly: boolean | undefined): string | undefined {
  if (friendly === undefined) return undefined;
  return friendly ? '#4FE06A' : '#D9483B';
}

/**
 * The right-hand name on a feed line, at the width this HUD actually has.
 *
 * The compact feed is about a third of the desktop one's width, and the only
 * lines that overflow it are the airdrops — "High-Explosive Rounds" wraps onto
 * a second row and pushes an older line out of the visible three. So a crate
 * gives its `shortName` here and a player never does: a call sign is already
 * the shortest true name for a player, and abbreviating one would be inventing
 * a second.
 *
 * Falls back to `victimName` whenever there is nothing shorter to say, which is
 * every kill line and every screen size above compact.
 */
function feedVictim(entry: KillFeedEntry): string {
  if (props.hud.touch && entry.victimShortName) return entry.victimShortName;
  return entry.victimName;
}

/** The roster, split so a glance answers "how is my side doing". */
const allies = computed(() => props.hud.players.filter((p) => p.friendly !== false));
const enemies = computed(() => props.hud.players.filter((p) => p.friendly === false));

/**
 * Kills per side, for the collapsed score.
 *
 * Summed here rather than taken from `hud.score`, which is the **round** score
 * and exists only in team battle — a deathmatch has no rounds and would show a
 * blank. Kills are on every `PlayerSnapshot` in every mode, so this is the one
 * figure that always means something, and it is what the full roster is already
 * showing a column of.
 */
const sideKills = computed(() => ({
  allies: allies.value.reduce((n, p) => n + (p.kills ?? 0), 0),
  enemies: enemies.value.reduce((n, p) => n + (p.kills ?? 0), 0),
}));

/**
 * The standing order panel.
 *
 * Shown whenever this side actually has AI tanks to command, and **defaulted to
 * "attack" rather than hidden** when nobody has ordered anything. That is not a
 * white lie for the sake of a tidy panel: attack *is* what an uncommanded team
 * does, so the panel reads correctly from the first frame and doubles as the
 * only place the player learns the keys exist.
 *
 * **Every order is listed, not only the standing one.** Showing just the active
 * order was reported as "I don't see the buttons for follow and hold position",
 * and that is exactly right: the panel opened on `1 Attack at will` and a player
 * who did not already know `2` and `3` existed had nowhere to find out. A
 * legend that only names the state you are already in teaches nothing.
 *
 * They are labels rather than clickable buttons on purpose. Aiming holds pointer
 * lock, so the cursor cannot reach a HUD control during a fight — a button that
 * did nothing whenever it mattered would be worse than a key you can read.
 *
 * The active row comes off the snapshot — the server's own — never off what this
 * client last asked for. See `HudState.teamOrder`.
 */
const ORDER_LABELS: Record<string, string> = {
  attack: 'Attack at will',
  follow: 'Follow me',
  hold: 'Hold position',
};
const DENSITY_LABELS: Record<string, string> = {
  close: 'Close ranks',
  normal: 'Normal spacing',
  spread: 'Spread out',
};
/** Which key each order sits on, so the legend cannot drift from the binding. */
const ORDER_KEYS: Record<string, string> = { attack: '1', follow: '2', hold: '3' };
const DENSITY_KEYS: Record<string, string> = { close: '4', normal: '5', spread: '6' };
/** Listed in key order, so the panel reads down the number row. */
const ORDER_IDS = ['attack', 'follow', 'hold'];
const DENSITY_IDS = ['close', 'normal', 'spread'];

const orders = computed(() => {
  const standing = props.hud.teamOrder;
  const order = standing?.order ?? 'attack';
  const density = standing?.density ?? 'normal';
  return {
    order,
    density,
    list: ORDER_IDS.map((id) => ({
      id,
      key: ORDER_KEYS[id] ?? '',
      label: ORDER_LABELS[id] ?? id,
      active: id === order,
    })),
    densities: DENSITY_IDS.map((id) => ({
      id,
      key: DENSITY_KEYS[id] ?? '',
      label: DENSITY_LABELS[id] ?? id,
      active: id === density,
    })),
  };
});

/** No AI on this side means nothing to command, so the panel stays off. */
/** Which mission this is, or null in PvP. Declared here because
 * `commandsAvailable` reads it — both are lazy computeds so the order did not
 * break, but a reference that only works by laziness is one refactor from a
 * temporal-dead-zone crash. */
const missionKind = computed(() => (props.hud.mode ? missionKindFor(props.hud.mode) : null));

/**
 * Whether a bot's name carries an `AI` tag. Missions only.
 *
 * In PvP the tag was noise at best. `ensureBots` tops both sides up to
 * `teamSizeFor(map)`, so in a deathmatch most of the roster is AI and labelling
 * it says little a player did not assume — while inviting them to discount half
 * the fight, which the fight does not reciprocate: a bot leads a moving target
 * with a real intercept solution and holds a range suited to its gun, and being
 * killed by one costs exactly what being killed by a person costs.
 *
 * In a **mission** the same tag is information a player acts on. The AI is the
 * content rather than the filler: allies the mission dealt them and enemies its
 * script is spending, and which tanks on your side are AI is who takes an order,
 * who will not improvise, and how much of the roster is the mission's rather than
 * yours.
 *
 * Read off `missionKind` — the mode, fixed at the arena's construction — rather
 * than off `hud.mission`. `CLAUDE.game.md` keeps those two apart deliberately:
 * `missionKind` is *does this arena play by mission rules*, and `mission` is *is
 * something currently driving the objectives*, which is a statement about how much
 * of the feature is built. Gating a label on the runtime would drop the tag in a
 * mission whose runtime had not published a snapshot yet.
 */
const showBotTag = computed(() => missionKind.value !== null);

/**
 * The convoy's id in an escort, or null.
 *
 * Its own computed because two unrelated things need it: the health bar reads
 * the convoy off the roster by it, and `commandsAvailable` has to exclude it
 * from the tanks the order keys can reach.
 */
const convoyId = computed(() =>
  props.hud.mission?.kind === 'escort' ? props.hud.mission.convoyId : null,
);

/**
 * Whether this player has AI tanks they can actually command.
 *
 * `some(isBot)` alone is wrong in an escort, and the convoy is why: it **is** a
 * friendly bot, so the panel lit up and offered six keys that did nothing. A
 * mission's `inputFor` intercepts the convoy before any anchor is read, so
 * ordering it to follow you is inert, and a HUD advertising ignored controls is
 * worse than one showing nothing — the player concludes the game is broken
 * rather than that the feature is absent.
 *
 * **`TEAM_ORDERS_ENABLED` is off, so today this is always false and the panel is
 * never drawn.** That is the same rule one step further along: with the keys inert
 * game-wide, a panel listing six of them is exactly the "advertising ignored
 * controls" failure above, and the roster test below is kept because it is the
 * condition that matters again the moment the flag flips back. See
 * `shared/protocol.ts`.
 *
 * **It is the convoy that is excluded, not missions.** Missions now start with a
 * friendly garrison and wave defence reinforces it, and those tanks are ordinary
 * bots whose mission `inputFor` answers `null` — so `driveBots` falls through to
 * `stepBot` with the player's own anchor and the order keys work on them with no
 * mission-side code. An earlier version of this excluded every mission outright,
 * which was right for exactly as long as the convoy was the only friendly bot in
 * one.
 */
const commandsAvailable = computed(
  () => TEAM_ORDERS_ENABLED && allies.value.some((a) => a.isBot && a.id !== convoyId.value),
);

/**
 * The round score, from the local player's point of view.
 *
 * Ours first, always. "3 : 5" means nothing until you know which number is
 * yours, and a scoreboard that ordered by team colour would read differently
 * depending on which side you were put on.
 */
const myScore = computed(() => {
  const score = props.hud.score;
  if (!score) return 0;
  return props.hud.localTeam === 'blue' ? score.blue : score.red;
});

const theirScore = computed(() => {
  const score = props.hud.score;
  if (!score) return 0;
  return props.hud.localTeam === 'blue' ? score.red : score.blue;
});

const scoreLine = computed(() => {
  if (!props.hud.score) return '';
  return `${myScore.value} - ${theirScore.value}`;
});

/** Whether the round just finished went our way. Null for a mutual wipe-out. */
/** Which of the three round outcomes is being announced, as a class. */
const roundToneClass = computed(() =>
  lastRoundWon.value === null ? 'drawn' : lastRoundWon.value ? 'won' : 'lost',
);

const lastRoundWon = computed<boolean | null>(() => {
  const w = props.hud.lastRoundWinner;
  if (w === undefined) return null;
  if (w === null) return null;
  return w === props.hud.localTeam;
});

/** One round from taking the match, either way. Worth saying out loud. */
const matchPoint = computed(() => {
  const score = props.hud.score;
  const target = props.hud.roundsToWin;
  if (!score || !target) return false;
  return score.red === target - 1 || score.blue === target - 1;
});

/**
 * The match-point line on the round banner, worded by whose point it is.
 * The top strip's "Match point" is a lamp; this is the one moment the player
 * is reading rather than fighting, so it can afford to say which way the axe
 * hangs. Empty when nobody is a round away, and the banner drops the line.
 */
const matchPointLine = computed(() => {
  const target = props.hud.roundsToWin;
  if (!props.hud.score || !target) return '';
  const mineAt = myScore.value === target - 1;
  const theirsAt = theirScore.value === target - 1;
  if (mineAt && theirsAt) return 'Next round decides the match';
  if (mineAt) return 'Match point — one more takes it';
  if (theirsAt) return 'Match point against you — hold the line';
  return '';
});

/**
 * The banner's eyebrow: what this is and how long it runs, the same
 * mode-then-room shape the result screen's eyebrow uses. Deliberately does
 * not name the round number — the intermission sits on the seam between two
 * rounds, so any number here would be off by one in somebody's reading.
 */
const roundEyebrow = computed(() => {
  const parts: string[] = [];
  if (props.hud.mode) parts.push(MODE_LABELS[props.hud.mode]);
  if (props.hud.roundsToWin) parts.push(`First to ${props.hud.roundsToWin}`);
  return parts.join(' · ');
});

/**
 * The last stretch of the count-in, said in seconds rather than a fraction —
 * the 8-second "deploying in N" phase and the 2-second "TO BATTLE!" beat are
 * one server-side freeze, split client-side purely for how it reads.
 */
const TO_BATTLE_SECONDS = 2;

/**
 * The count-in's last two seconds read as a beat, not a countdown — there is
 * nothing left to plan, so the numeral gives way to a single line of intent.
 */
const toBattle = computed(
  () =>
    props.hud.roundStartsInSeconds !== undefined &&
    props.hud.roundStartsInSeconds > 0 &&
    props.hud.roundStartsInSeconds <= TO_BATTLE_SECONDS,
);

/* ─── Missions ─────────────────────────────────────────────────────────────
 *
 * Three strips, one per kind, in the **top-centre** slot. That slot is free in a
 * mission for a structural reason rather than a convenient one: what lives there
 * is the round score, and rounds are team-battle only — so no mission can ever
 * collide with it, and the tutorial (which uses the same slot) is never a
 * mission either.
 *
 * They are built **per kind** rather than as one strip with everything optional,
 * mirroring `MissionSnapshot`'s own discriminated union: a wave counter cannot
 * then be read during an escort. Only wave defence is produced by the server
 * today, so the other two are written against the wire's shape and degrade to
 * nothing at all until a snapshot carries them — which is what the `?.kind`
 * guard does, not a feature flag somebody has to remember to clear.
 *
 * Every number here is a pure function of the snapshot and its tick. Nothing on
 * this component counts down.
 */
const missionTick = computed(() => props.hud.missionTick ?? 0);

/** Which mission this is, for the wording on the end-of-battle banner. */
/**
 * What the banner says under MISSION COMPLETE / FAILED.
 *
 * Per **kind**, because one line cannot cover three missions and the generic one
 * was wrong two thirds of the time: "the objective was lost" is exactly right
 * for an escort, and nonsense for wave defence, where nothing was lost except
 * you — the redoubt has no objective to lose, you were simply overrun. Seen in
 * the first real playthrough, where standing still through wave one produced a
 * failure screen blaming a convoy that was never on the map.
 */
const MISSION_OUTCOME_LINES: Record<MissionKind, { won: string; lost: string }> = {
  // The tutorial has no losing branch on the server — a destroyed learner is put
  // back on their feet — so `lost` here is unreachable today and is written
  // anyway, because the record is exhaustive and a mission that grows one later
  // must not fall back to a convoy's wording. That is the exact defect this
  // table replaced.
  tutorial: { won: 'Training complete.', lost: 'The drills were cut short.' },
  escort: { won: 'The convoy reached the depot.', lost: 'The convoy was destroyed.' },
  ctf: { won: 'Every core is yours.', lost: 'Your raiders are spent.' },
  'wave-defence': { won: 'The redoubt held.', lost: 'The redoubt was overrun.' },
};

const missionOutcomeLine = computed(() => {
  const kind = missionKind.value;
  if (!kind) return '';
  const lines = MISSION_OUTCOME_LINES[kind];
  return props.hud.won ? lines.won : lines.lost;
});

/**
 * The mode's display name for the result screen's eyebrow — the same names the
 * battle picker sells these under, so the debrief describes the sortie in the
 * words it was chosen in. A record over `MatchMode` rather than a fallback
 * chain, so a seventh mode is a compile error here instead of a blank line.
 */
const MODE_LABELS: Record<MatchMode, string> = {
  deathmatch: 'Deathmatch',
  'team-battle': 'Team battle',
  tutorial: 'Basic training',
  escort: 'Convoy Run',
  ctf: 'Relay Raid',
  'wave-defence': 'Last Stand',
};

/**
 * What the sortie was, over the banner: the mode, and the room when named.
 *
 * The tutorial's own arena is always named literally `'Basic Training'`
 * server-side (`ArenaService.tutorial`) — it is a private practice yard, not a
 * map, so there is no second name to add. Every other mode's `arenaName` is
 * either a real map or a player-chosen room name, distinct from the mode
 * label, so the pair is worth showing there. Pushing it unconditionally read
 * "Basic training · Basic Training" — the same fact stated twice rather than
 * mode-plus-place.
 */
const resultEyebrow = computed(() => {
  const parts: string[] = [];
  if (props.hud.mode) parts.push(MODE_LABELS[props.hud.mode]);
  if (props.arenaName && props.hud.mode !== 'tutorial') parts.push(props.arenaName);
  return parts.join(' · ');
});

/**
 * **Why** the deathmatch ended, and nothing else.
 *
 * It used to be one sentence carrying the reason and the figures together —
 * "Time up — 3:5 on kills." The figures are the thing a player looks for first
 * and they were the hardest part of that line to find: buried mid-sentence, in
 * body text, with the two sides told apart by their order alone. They are their
 * own element now (`outcomeScore`), coloured by side, and this is left saying
 * only the part that is genuinely a sentence.
 *
 * Which condition fired is inferred from the roster rather than carried on the
 * wire: a tank at the kill target means the target ended it, and anything else
 * was the clock, because those are the only two ways a deathmatch ends.
 */
const deathmatchOutcomeLine = computed(() => {
  if (props.hud.mode !== 'deathmatch' || !props.hud.outcome) return '';
  const targetReached = props.hud.players.some((p) => (p.kills ?? 0) >= DEATHMATCH_KILL_TARGET);
  return targetReached ? 'Kill target reached' : 'Time up';
});

/**
 * The final figures as a pair, ours first, for the result screen's own score.
 *
 * One computed for both modes because they are the same fact in different
 * currency — a deathmatch is settled on kills and a team battle on rounds — and
 * the screen asks the same question of each. A mission has no numeric pair at
 * all, so it gets none rather than a fabricated one.
 */
const outcomeScore = computed<{ ours: number; theirs: number } | null>(() => {
  if (missionKind.value || !props.hud.outcome) return null;
  if (props.hud.mode === 'deathmatch') {
    let ours = 0;
    let theirs = 0;
    for (const p of props.hud.players) {
      const kills = p.kills ?? 0;
      if (p.isLocal || p.friendly) ours += kills;
      else theirs += kills;
    }
    return { ours, theirs };
  }
  if (!props.hud.score) return null;
  return { ours: myScore.value, theirs: theirScore.value };
});

const waveStrip = computed(() => {
  const m = props.hud.mission;
  if (m?.kind !== 'wave-defence') return null;
  // Present only during a lull, so its presence *is* "no wave is up".
  const next = m.nextWaveAtTick;
  return {
    wave: waveOnScreen(m.wave, next !== undefined),
    total: WAVE_COUNT,
    // Counted here from the roster the client already holds, never asked for.
    enemies: enemiesLeft(props.hud.players),
    countdown: next === undefined ? null : formatClock(secondsUntil(next, missionTick.value)),
  };
});

const escortStrip = computed(() => {
  const m = props.hud.mission;
  if (m?.kind !== 'escort') return null;
  // The convoy is an ordinary tank on the field, found by the id the mission
  // names. Absent while it has not been dealt in yet — the bar then reads as
  // unknown rather than as a full one, because a convoy that has not arrived and
  // a convoy at full health are different things.
  const convoy = props.hud.players.find((p) => p.id === m.convoyId);
  const maxHealth = convoy?.maxHealth ?? 0;
  const health = convoy?.health ?? 0;
  return {
    leg: legOnScreen(m.leg, m.legs),
    legs: m.legs,
    // Derived, never a second field on the wire — see `MissionSnapshot`'s
    // escort variant. The strip and the chip over the vehicle are then two
    // renderings of one fact rather than two facts that can disagree.
    halted: convoyHalted(m.convoy),
    status: m.convoy,
    statusLabel: convoyStatusLabel(m.convoy),
    known: convoy !== undefined && maxHealth > 0,
    health,
    maxHealth,
    fraction: maxHealth > 0 ? Math.max(0, Math.min(1, health / maxHealth)) : 0,
    lost: convoy?.alive === false,
  };
});

/** What the core is doing, in the words the feed uses for the same events. */
const FLAG_LABELS = {
  home: 'In their fort',
  carried: 'Carried',
  dropped: 'Dropped',
} as const;

const ctfStrip = computed(() => {
  const m = props.hud.mission;
  if (m?.kind !== 'ctf') return null;
  const flag = m.flag;
  const localId = props.hud.players.find((p) => p.isLocal)?.id;
  return {
    captures: m.captures,
    target: CTF_CAPTURES_TO_WIN,
    lives: m.livesLeft,
    state: flag.state,
    label: FLAG_LABELS[flag.state],
    // Ours only when the carrier is this player. `carrierId` is a player id and
    // never a side, so this stays right whichever side the server put us on.
    mine: flag.state === 'carried' && flag.carrierId === localId,
    returning:
      flag.state === 'dropped' && flag.droppedAtTick !== undefined
        ? formatClock(flagReturnSeconds(flag.droppedAtTick, missionTick.value))
        : null,
  };
});

const loaded = computed(() => combat.value.reloadProgress >= 1);
const charging = computed(() => combat.value.chargeProgress > 0);

const respawnCountdown = computed(() => Math.max(1, Math.ceil(combat.value.respawnInSeconds)));
</script>

<template>
  <div class="hud" :class="{ compact: !!hud.touch }" :style="compactVars">
    <TouchControls
      v-if="hud.touch"
      :view="hud.touch"
      :ready="!!hud.alive && (hud.reloadProgress ?? 0) >= 1"
    />
    <div
      v-if="combat.alive"
      class="reticle"
      :class="{ loaded, charging, slewing: !!combat.gunMarker }"
      data-test="reticle"
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100">
        <path class="ring-track" d="M 24 50 A 26 26 0 0 0 76 50" path-length="100" />
        <path
          class="ring"
          d="M 24 50 A 26 26 0 0 0 76 50"
          path-length="100"
          stroke-dasharray="100"
          :stroke-dashoffset="100 * (1 - combat.reloadProgress)"
        />
        <line x1="50" y1="82" x2="50" y2="92" />
        <line x1="8" y1="50" x2="18" y2="50" />
        <line x1="82" y1="50" x2="92" y2="50" />
        <circle class="pip" cx="50" cy="50" r="1.6" />
      </svg>
    </div>
    <div
      v-if="combat.alive && combat.gunMarker"
      class="gun-marker"
      :class="{ clamped: combat.gunMarker.clamped }"
      :style="{ left: `${combat.gunMarker.x}px`, top: `${combat.gunMarker.y}px` }"
      data-test="gun-marker"
      aria-hidden="true"
    >
      <span class="diamond"></span>
    </div>
    <div
      v-if="combat.alive && combat.aimReticle"
      class="aim-reticle"
      :class="{ clamped: combat.aimReticle.clamped }"
      :style="{ left: `${combat.aimReticle.x}px`, top: `${combat.aimReticle.y}px` }"
      data-test="aim-reticle"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24">
        <line x1="2" y1="12" x2="9" y2="12" />
        <line x1="15" y1="12" x2="22" y2="12" />
        <line x1="12" y1="2" x2="12" y2="9" />
        <line x1="12" y1="15" x2="12" y2="22" />
      </svg>
    </div>

    <div
      v-if="combat.hitMarkers.length"
      class="hit-markers"
      data-test="hit-markers"
      aria-hidden="true"
    >
      <span
        v-for="(marker, i) in combat.hitMarkers"
        :key="marker.id"
        class="hit-marker"
        data-test="hit-marker"
        :data-damage="Math.round(marker.damage)"
        :style="{ '--jitter': `${(i - (combat.hitMarkers.length - 1) / 2) * 1.1}rem` }"
      >-{{ Math.round(marker.damage) }}</span>
    </div>

    <div
      v-if="combat.alive && combat.objective"
      class="objective"
      :class="{
        clamped: combat.objective.clamped,
        reached: combat.objective.reached,
        left: combat.objective.side === -1,
        right: combat.objective.side === 1,
      }"
      :style="{ left: `${combat.objective.x}px`, top: `${combat.objective.y}px` }"
      data-test="objective-marker"
      :data-reached="combat.objective.reached ? 'true' : 'false'"
      :data-clamped="combat.objective.clamped ? 'true' : 'false'"
    >
      <svg class="objective-glyph" viewBox="0 0 24 24" aria-hidden="true">
        <path class="chev" d="M4 8 L12 17 L20 8" />
        <circle class="ring" cx="12" cy="12" r="7" />
        <path class="tick" d="M8 12.5 L11 15.5 L16.5 9.5" />
      </svg>
      <span class="objective-label" data-test="objective-distance">
        {{ combat.objective.reached ? 'ON POSITION' : `${Math.round(combat.objective.distanceM)} m` }}
      </span>
    </div>

    <div
      v-for="plate in combat.nameplates"
      :key="plate.id"
      class="nameplate"
      :class="{ hostile: plate.friendly === false }"
      :style="{ transform: `translate(-50%, -100%) translate(${plate.x}px, ${plate.y}px)` }"
    >
      <span
        v-if="plate.tag === 'convoy'"
        class="plate-tag"
        :class="`is-${plate.status ?? 'rolling'}`"
        data-test="convoy-tag"
      >
        <b>CONVOY</b>
        <i data-test="convoy-status">{{ convoyStatusLabel(plate.status) }}</i>
      </span>
      <TitleBadge :title-id="plate.titleId" variant="plate" />
      <span class="plate-name">
        {{ plate.nickname }}<i v-if="plate.isBot && showBotTag" class="ai" title="AI">AI</i>
      </span>
      <span class="plate-row">
        <span class="plate-bar">
          <i
            :style="{
              width: `${Math.max(0, Math.min(1, plate.health / plate.maxHealth)) * 100}%`,
              background: plateColour(plate),
            }"
          ></i>
        </span>
        <span class="plate-hp" data-test="plate-hp" :style="{ color: plateColour(plate) }">
          {{ Math.max(0, Math.ceil(plate.health)) }}
        </span>
      </span>
    </div>

    <button
      v-if="hud.touch"
      class="score-pill"
      data-test="hud-score-pill"
      :aria-expanded="rosterOpen"
      @click="rosterOpen = !rosterOpen"
    >
      <span class="score-pill-us">{{ sideKills.allies }}</span>
      <span class="score-pill-dash">–</span>
      <span class="score-pill-them">{{ sideKills.enemies }}</span>
    </button>

    <div
      v-if="hud.touch && rosterOpen"
      class="roster-scrim"
      data-test="hud-roster-scrim"
      @pointerdown="rosterOpen = false"
    />

    <div class="roster" :class="{ open: rosterOpen }" data-test="hud-roster">
      <span v-if="arenaName" class="label arena-label" data-test="hud-arena">{{ arenaName }}</span>
      <span class="label">Allies · {{ allies.length }}</span>
      <ul>
        <li v-for="p in allies" :key="p.id" :class="{ local: p.isLocal, down: p.alive === false }">
          <span class="who">
            <span class="who-name">
              {{ p.isLocal ? nickname : p.nickname }}<i v-if="p.isBot && showBotTag" class="ai">AI</i>
            </span>
          </span>
          <span class="score">{{ p.kills ?? 0 }}/{{ p.deaths ?? 0 }}</span>
        </li>
      </ul>
      <span class="label enemy-label">Enemies · {{ enemies.length }}</span>
      <ul>
        <li v-for="p in enemies" :key="p.id" class="hostile" :class="{ down: p.alive === false }">
          <span class="who">
            <span class="who-name">{{ p.nickname }}<i v-if="p.isBot && showBotTag" class="ai">AI</i></span>
          </span>
          <span class="score">{{ p.kills ?? 0 }}/{{ p.deaths ?? 0 }}</span>
        </li>
      </ul>
    </div>

    <RadarScope :radar="hud.radar" :compact="!!hud.touch" />

    <div class="readout">
      <div
        v-if="!hud.touch"
        class="ping"
        :class="{ bad: hud.latencyMs > 120 }"
      >{{ hud.latencyMs }} ms</div>
      <div
        v-if="!hud.touch"
        class="fps"
        :class="{ bad: hud.fps > 0 && hud.fps < 30 }"
        data-test="hud-fps"
      >
        {{ hud.fps }} fps
      </div>
      <button
        v-if="!hud.touch"
        class="settings-toggle"
        data-test="settings-toggle"
        :class="{ active: menuOpen }"
        :aria-expanded="menuOpen"
        title="Settings, resume and leave"
        @click="menuOpen = !menuOpen"
      >
        Settings
      </button>
      <button v-if="!hud.touch" @click="askToLeave()">Leave</button>
      <button
        v-else
        class="menu-button"
        data-test="hud-menu-button"
        :class="{ active: menuOpen }"
        :aria-expanded="menuOpen"
        aria-label="Menu"
        title="Menu, settings and leave"
        @click="menuOpen = !menuOpen"
      >
        <GearIcon />
      </button>
    </div>

    <SettingsPanel
      v-if="menuOpen"
      eyebrow="Battle menu"
      data-test="escape-menu"
      @close="closeMenu"
    >
      <template #actions>
        <p class="menu-sub">
          The arena keeps running. Your tank holds position and holds fire.
        </p>
        <div class="menu-buttons">
          <button class="menu-resume" data-test="menu-resume" @click="closeMenu">
            Resume
          </button>
          <button class="menu-leave" data-test="menu-leave" @click="askToLeave()">
            {{ hud.tutorial ? 'Leave · start the drills over' : 'Leave battle' }}
          </button>
        </div>
        <p v-if="leavingCosts && !hud.tutorial" class="menu-cost" data-test="leave-cost">
          Leaving now earns you nothing at all — a sortie only pays out once the
          battle is decided. Your kills, deaths and quest progress still stand.
        </p>
      </template>
    </SettingsPanel>

    <div
      v-if="hud.tutorial && !hud.outcome"
      class="tutorial"
      data-test="tutorial"
      :data-step="hud.tutorial.id"
    >
      <p class="tutorial-step">
        Basic training · Drill {{ hud.tutorial.stepNumber }} of {{ hud.tutorial.stepCount }}
      </p>
      <h2 class="tutorial-title" data-test="tutorial-title">{{ hud.tutorial.title }}</h2>
      <p class="tutorial-blurb">{{ hud.tutorial.blurb }}</p>
      <ul v-if="hud.tutorial.goals.length" class="tutorial-goals">
        <li
          v-for="goal in hud.tutorial.goals"
          :key="goal.label"
          class="tutorial-goal"
          :class="{ done: goal.done }"
          data-test="tutorial-goal"
          :data-done="goal.done ? 'true' : 'false'"
        >
          <span class="tutorial-tick" aria-hidden="true">{{ goal.done ? '✓' : '○' }}</span>
          <span class="tutorial-label">{{ goal.label }}</span>
          <span class="tutorial-count" data-test="tutorial-goal-count">{{ goal.text }}</span>

          <span class="tutorial-keys" data-test="tutorial-keys">
            <template v-for="(k, i) in goal.keys" :key="k.cap">
              <span v-if="i > 0" class="tutorial-or" aria-hidden="true">or</span>
              <KeyCap v-bind="k" :pulse="!goal.done" />
            </template>
          </span>
        </li>
      </ul>
      <p v-if="hud.tutorial.note" class="tutorial-note" data-test="tutorial-note">
        {{ hud.tutorial.note }}
      </p>
      <p v-if="hud.touch" class="tutorial-hint">
        <GearIcon class="tutorial-hint-gear" /> to leave · nothing here is earned
      </p>
      <p v-else class="tutorial-hint">
        <kbd>Esc</kbd> to leave and start the drills over · nothing here is earned
      </p>
    </div>

    <div v-if="combat.buffs.length" class="buffs" data-test="buffs">
      <div
        v-for="b in combat.buffs"
        :key="b.kind"
        class="buff"
        :data-test="`buff-${b.kind}`"
        :style="{ borderColor: b.colour }"
      >
        <span class="buff-name" :style="{ color: b.colour }">{{ b.name }}</span>
        <span class="buff-time">{{ Math.ceil(b.secondsLeft) }}s</span>
      </div>
    </div>

    <div v-if="commandsAvailable" class="orders" data-test="orders">
      <div
        v-for="o in orders.list"
        :key="o.id"
        class="orders-line"
        :class="{ active: o.active }"
        :data-order="o.id"
        :data-active="o.active ? 'yes' : 'no'"
        :data-test="o.active ? 'order' : undefined"
      >
        <span class="orders-key" aria-hidden="true">{{ o.key }}</span>
        <span class="orders-label">{{ o.label }}</span>
      </div>
      <div
        v-for="d in orders.densities"
        :key="d.id"
        class="orders-line subtle"
        :class="{ active: d.active }"
        :data-density="d.id"
        :data-active="d.active ? 'yes' : 'no'"
        :data-test="d.active ? 'density' : undefined"
      >
        <span class="orders-key" aria-hidden="true">{{ d.key }}</span>
        <span class="orders-label">{{ d.label }}</span>
      </div>
    </div>

    <div class="kill-feed">
      <div
        v-for="entry in combat.killFeed"
        :key="entry.id"
        class="kill"
        :style="{ borderColor: sideColour(entry.killerFriendly) }"
      >
        <span class="killer" :style="{ color: sideColour(entry.killerFriendly) }">
          <TitleBadge v-if="!hud.touch" :title-id="entry.killerTitleId" variant="feed" />{{ entry.killerName }}
        </span>
        <span class="glyph" aria-hidden="true">▸</span>
        <span class="victim" :style="{ color: sideColour(entry.victimFriendly) }">
          <TitleBadge v-if="!hud.touch" :title-id="entry.victimTitleId" variant="feed" />{{ feedVictim(entry) }}
        </span>
      </div>
    </div>

    <!--
      Loader on top, health under it, speed in the middle of the tally.

      The order is the order the eye needs them in a firefight: whether the gun
      is ready decides the next half second, how much health is left decides the
      next few, and the speed sits in the one place already being glanced at for
      the score. Armour rides on the health bar rather than getting a bar of its
      own — it is a flat fraction of every hit, not a pool that drains, and a
      second bar beside health would say otherwise.
    -->
    <div class="status">
      <div class="bar reload" :class="{ loaded }">
        <i :style="{ width: `${combat.reloadProgress * 100}%` }"></i>
        <span class="bar-text">{{ loaded ? 'LOADED' : 'RELOADING' }}</span>
      </div>
      <div v-if="charging" class="bar charge">
        <i :style="{ width: `${combat.chargeProgress * 100}%` }"></i>
        <span class="bar-text">CHARGE {{ Math.round(combat.chargeProgress * 100) }}%</span>
      </div>
      <div class="bar health">
        <i :style="{ width: `${healthFraction * 100}%`, background: healthColour(healthFraction) }"></i>
        <span class="bar-text">{{ combat.health }} / {{ combat.maxHealth }}</span>
        <span v-if="hud.armourPercent > 0" class="bar-armour" data-test="hud-armour">
          {{ hud.armourPercent }}% ARMOUR
        </span>
      </div>
      <div class="tally">
        <span><strong>{{ combat.kills }}</strong> kills</span>
        <span class="tally-speed" data-test="hud-speed">
          <strong>{{ hud.speedKph }}</strong><span class="unit">km/h</span>
        </span>
        <span><strong>{{ combat.deaths }}</strong> deaths</span>
      </div>
    </div>

    <div class="reticle" :class="{ ready: loaded }" aria-hidden="true"></div>

    <div class="damage" :style="{ opacity: combat.damageFlash * 0.7 }" aria-hidden="true"></div>

    <div v-if="hud.score" class="rounds" data-test="round-score" :class="{ 'match-point': matchPoint }">
      <span class="rounds-score">{{ scoreLine }}</span>
      <span class="rounds-sub">
        {{ matchPoint ? 'Match point' : `Round ${hud.round} · first to ${hud.roundsToWin}` }}
      </span>
    </div>

    <div v-else-if="hud.matchSecondsLeft !== undefined" class="rounds" data-test="match-clock">
      <span class="rounds-score">{{ formatClock(hud.matchSecondsLeft) }}</span>
      <span class="rounds-sub">First to {{ DEATHMATCH_KILL_TARGET }} kills</span>
    </div>

    <div
      v-if="waveStrip"
      class="mission"
      data-test="mission"
      data-mission="wave-defence"
      :data-wave="waveStrip.wave"
      :data-enemies="waveStrip.enemies"
    >
      <span class="mission-headline">WAVE {{ waveStrip.wave }} / {{ waveStrip.total }}</span>
      <span v-if="waveStrip.countdown" class="mission-sub" data-test="mission-countdown">
        Next wave in {{ waveStrip.countdown }}
      </span>
      <span v-else class="mission-sub" data-test="mission-enemies">
        {{ waveStrip.enemies }} {{ waveStrip.enemies === 1 ? 'enemy' : 'enemies' }} remaining
      </span>
    </div>

    <div
      v-else-if="escortStrip"
      class="mission"
      data-test="mission"
      data-mission="escort"
      :data-leg="escortStrip.leg"
      :data-halted="escortStrip.halted ? 'yes' : 'no'"
    >
      <span class="mission-headline">LEG {{ escortStrip.leg }} / {{ escortStrip.legs }}</span>
      <span v-if="escortStrip.known" class="mission-bar" data-test="convoy-health">
        <i
          :style="{
            width: `${escortStrip.fraction * 100}%`,
            background: healthColour(escortStrip.fraction),
          }"
        ></i>
        <span class="mission-bar-text">
          CONVOY {{ escortStrip.health }} / {{ escortStrip.maxHealth }}
        </span>
      </span>
      <span v-if="escortStrip.lost" class="mission-alarm" data-test="convoy-lost">CONVOY LOST</span>
      <span
        v-else-if="escortStrip.status === 'waiting'"
        class="mission-sub"
        data-test="convoy-waiting"
      >
        Waiting for you — stay within {{ CONVOY_ESCORT_RADIUS }} m
      </span>
      <span v-else-if="escortStrip.halted" class="mission-alarm" data-test="convoy-halted">
        HALTED — {{ escortStrip.status === 'under-attack' ? 'UNDER ATTACK' : 'ENEMIES ON THE ROAD' }}
      </span>
      <span v-else class="mission-sub">{{ escortStrip.statusLabel }}</span>
    </div>

    <div
      v-else-if="ctfStrip"
      class="mission"
      data-test="mission"
      data-mission="ctf"
      :data-captures="ctfStrip.captures"
      :data-flag="ctfStrip.state"
    >
      <span class="mission-headline">
        CAPTURES {{ ctfStrip.captures }} / {{ ctfStrip.target }}
      </span>
      <span class="mission-sub" data-test="flag-state">
        <em :class="`flag-${ctfStrip.state}`">{{ ctfStrip.mine ? 'You have it' : ctfStrip.label }}</em>
        <template v-if="ctfStrip.returning"> · resets in {{ ctfStrip.returning }}</template>
      </span>
      <span class="mission-sub" data-test="mission-lives">
        {{ ctfStrip.lives }} {{ ctfStrip.lives === 1 ? 'life' : 'lives' }} left
      </span>
    </div>

    <div v-if="settlement" class="result forfeit" data-test="forfeit">
      <div class="result-col">
        <header class="result-head">
          <span class="result-eyebrow">{{ arenaName ?? 'Battle' }} · abandoned</span>
          <h2 class="result-title lost">
            <b class="result-word">LEFT THE BATTLE</b>
          </h2>
          <span class="result-sub">
            {{
              settlement.forfeited
                ? 'Nothing banked — the battle had not been decided'
                : 'Banked in full'
            }}
          </span>
        </header>

        <div class="forfeit-figures">
          <div class="forfeit-plate">
            <b data-test="forfeit-credits">{{ settlement.credits.toLocaleString('en-GB') }}</b>
            <span>credits</span>
          </div>
          <div class="forfeit-plate">
            <b data-test="forfeit-xp">{{ settlement.xp.toLocaleString('en-GB') }}</b>
            <span>experience</span>
          </div>
          <div class="forfeit-plate">
            <b>{{ settlement.kills }} / {{ settlement.deaths }}</b>
            <span>kills / deaths</span>
          </div>
        </div>

        <p v-if="settlement.forfeited" class="forfeit-note">
          You left before the battle was decided, so this sortie paid nothing. The
          kills and deaths above still count toward your record, as does your
          progress on the daily quests.
        </p>

        <button class="result-leave" data-test="forfeit-leave" @click="$emit('leave')">
          Return to garage
        </button>
      </div>
    </div>

    <div
      v-else-if="hud.outcome"
      class="result"
      :class="{ won: hud.won, draw: !missionKind && hud.outcome.winner === null }"
      data-test="outcome"
    >
      <video
        v-if="!resultReducedMotion"
        class="result-video"
        :class="{ ready: resultVideoReady }"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        aria-hidden="true"
        @playing="resultVideoReady = true"
      >
        <source :src="resultVideoSources.webm" type="video/webm" />
        <source :src="resultVideoSources.mp4" type="video/mp4" />
      </video>
      <div v-if="!resultReducedMotion" class="result-scrim" aria-hidden="true"></div>
      <div class="result-col">
        <header class="result-head">
          <span v-if="resultEyebrow" class="result-eyebrow">{{ resultEyebrow }}</span>
          <span class="result-title-row">
            <span
              v-if="outcomeScore"
              class="result-title-score us"
              data-test="result-score-us"
            >
              {{ outcomeScore.ours }}
            </span>
            <span
              class="result-title"
              :class="{ won: hud.won, draw: !missionKind && hud.outcome.winner === null }"
            >
              <b class="result-word">
                <template v-if="missionKind">
                  {{ hud.won ? 'MISSION COMPLETE' : 'MISSION FAILED' }}
                </template>
                <template v-else>
                  {{ hud.won ? 'VICTORY' : hud.outcome.winner === null ? 'DRAW' : 'DEFEAT' }}
                </template>
              </b>
            </span>
            <span
              v-if="outcomeScore"
              class="result-title-score them"
              data-test="result-score-them"
            >
              {{ outcomeScore.theirs }}
            </span>
          </span>
          <!--
            The score sits on the headline row now — flanking the verdict word
            rather than under it — so this line is only the reason it ended,
            plus the unit those flanking figures are counted in ("kills" or
            "rounds", which the numbers alone don't say).

            The reason and the score used to be one *sentence* ("Time up —
            3:5 on kills."), which buried the two numbers a player looks for
            first in body text. They moved onto the headline as their own
            elements, and green/red still says whose is whose — the same two
            colours the roster, the kill feed and the floating nameplates
            already use, so nothing new has to be learnt to read it.

            One row here rather than two because this screen's hard rule is
            that it fits one screen everywhere (`result-fit`), and a second
            line costs 13px on a landscape phone and 28px on a 660px laptop
            window — which is a scoreboard row, on the screen whose job is
            showing the scoreboard. Moving the score onto the headline row
            paid for itself here rather than costing anything.
          -->
          <p class="result-line">
            <span v-if="missionKind" class="result-sub">
              {{ missionOutcomeLine }}
            </span>
            <span v-else-if="hud.mode === 'deathmatch'" class="result-sub">
              {{ deathmatchOutcomeLine }}
            </span>
            <span v-else class="result-sub">
              {{
                hud.outcome.winner === null
                  ? 'Neither side could take it'
                  : hud.won
                    ? 'Match won'
                    : 'Match lost'
              }}
            </span>
            <span v-if="outcomeScore" class="result-score-unit" data-test="result-score">
              {{ hud.mode === 'deathmatch' ? 'kills' : 'rounds' }}
            </span>
          </p>
        </header>
        <BattleSummary :hud="hud" :nickname="nickname" />
        <div class="result-actions">
          <button class="result-leave" data-test="result-leave" @click="$emit('leave')">
            Return to garage
          </button>
          <ResultFuelButton v-if="hud.touch && hud.mode !== 'tutorial'" />
        </div>
      </div>
    </div>

    <div v-else-if="hud.roundStartsInSeconds" class="round-end" data-test="round-start">
      <div v-if="toBattle" class="round-banner counting imminent" data-test="round-start-imminent">
        <span class="to-battle-text">TO BATTLE!</span>
      </div>
      <div v-else class="round-banner counting">
        <span v-if="roundEyebrow" class="round-eyebrow">{{ roundEyebrow }}</span>
        <span class="round-headline">
          <b class="round-headline-word round-count" data-test="round-start-seconds">
            {{ hud.roundStartsInSeconds }}
          </b>
        </span>
        <span class="round-stage">
          {{ hud.round && hud.round > 1 ? `ROUND ${hud.round}` : 'MATCH STARTING' }}
        </span>
        <span class="round-note">
          Deploying in <b>{{ hud.roundStartsInSeconds }}</b>s — hold fire
        </span>
      </div>
    </div>

    <div v-else-if="hud.intermissionSeconds" class="round-end" data-test="intermission">
      <div class="round-banner" :class="roundToneClass">
        <span v-if="roundEyebrow" class="round-eyebrow">{{ roundEyebrow }}</span>
        <span class="round-headline">
          <b class="round-headline-word">
            {{ lastRoundWon === null ? 'ROUND DRAWN' : lastRoundWon ? 'ROUND WON' : 'ROUND LOST' }}
          </b>
        </span>
        <div v-if="hud.score && hud.roundsToWin" class="round-tally" data-test="round-tally">
          <span class="pips mine" aria-hidden="true">
            <i
              v-for="n in hud.roundsToWin"
              :key="n"
              :class="{ lit: n <= myScore, fresh: lastRoundWon === true && n === myScore }"
            ></i>
          </span>
          <span class="tally-score">
            <b>{{ myScore }}</b>
            <em>:</em>
            <b>{{ theirScore }}</b>
          </span>
          <span class="pips theirs" aria-hidden="true">
            <i
              v-for="n in hud.roundsToWin"
              :key="n"
              :class="{ lit: n <= theirScore, fresh: lastRoundWon === false && n === theirScore }"
            ></i>
          </span>
        </div>
        <span v-if="matchPointLine" class="round-alert" data-test="match-point-line">
          {{ matchPointLine }}
        </span>
        <span class="round-note">
          Next round in <b>{{ hud.intermissionSeconds }}</b>s
        </span>
      </div>
    </div>

    <div v-else-if="!combat.alive" class="destroyed" data-test="destroyed">
      <span class="destroyed-title">DESTROYED</span>
      <span v-if="hud.killedBy" class="destroyed-killer" data-test="destroyed-killer">
        Destroyed by
        <TitleBadge :title-id="hud.killedBy.titleId" variant="feed" />
        <strong :style="{ color: sideColour(hud.killedBy.friendly) }">{{ hud.killedBy.name }}</strong>
      </span>
      <span v-if="missionKind === 'wave-defence'" class="destroyed-sub">
        Back on your feet when the next wave arrives.
      </span>
      <span v-else-if="missionKind === 'ctf'" class="destroyed-sub">
        {{
          hud.mission?.kind === 'ctf' && hud.mission.livesLeft <= 0
            ? 'No lives left on the raid.'
            : 'Rejoining the raid…'
        }}
      </span>
      <span v-else-if="missionKind === 'escort'" class="destroyed-sub">
        Back on the road behind the convoy…
      </span>
      <span v-else-if="hud.mode === 'team-battle'" class="destroyed-sub">
        {{
          hud.eliminated
            ? 'No tanks left on your side.'
            : `Respawning as your teammate in ${respawnCountdown}s`
        }}
      </span>
      <span v-else class="destroyed-sub">Redeploying in {{ respawnCountdown }}</span>
    </div>

    <div v-if="hud.tookOver && combat.alive" class="took-over" data-test="took-over">
      Now driving <strong>{{ hud.tookOver }}</strong>
    </div>

    <div
      v-if="combat.alive && combat.spawnZoneWarning"
      class="spawn-zone-warning"
      :class="{ damaging: combat.spawnZoneWarning.damaging }"
      data-test="spawn-zone-warning"
      aria-hidden="true"
    >
      <span class="spawn-zone-warning-text">Enemy spawn — move out</span>
      <span
        v-if="!combat.spawnZoneWarning.damaging"
        class="spawn-zone-warning-count"
        data-test="spawn-zone-warning-seconds"
      >{{ combat.spawnZoneWarning.secondsLeft }}</span>
      <span v-else class="spawn-zone-warning-count damaging" data-test="spawn-zone-warning-damaging">
        Taking damage
      </span>
    </div>
  </div>
</template>

<style scoped>
.hud {
  position: absolute;
  inset: 0;
  /* The canvas underneath needs the mouse — the HUD only paints. */
  pointer-events: none;
  font-size: 0.8rem;
  overflow: hidden;
}

.roster,
.readout,
.status {
  position: absolute;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: rgba(11, 13, 17, 0.72);
  backdrop-filter: blur(4px);
}

.roster {
  top: 1rem;
  left: 1rem;
  min-width: 170px;
}

.label {
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-400);
}

ul {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.2rem;
}

li {
  display: flex;
  /*
   * Bottom-aligned, so the kills/deaths column lines up with the *nickname*
   * rather than with the middle of a two-line cell. The pair "who — how they are
   * doing" is the row; a title is an adornment above it, and letting it drag the
   * score up alongside itself makes the number look like it belongs to the badge.
   * Identical for the untitled rows, which are one line tall.
   */
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.8rem;
}

/*
 * A titled player takes two lines here, an untitled one takes the one it always
 * took. The badge is stacked over the name rather than in front of it so the
 * roster reads as the same object as the nameplate — and because the panel is
 * 170 px wide, where `QUARTERMASTER` beside a nickname would either wrap
 * unpredictably or push the kills column off the end.
 *
 * `align-items: start` rather than `center`: the badge is narrower than most
 * nicknames, and a centred one drifts around as the roster changes, which turns
 * a scoreboard into something that moves.
 */
/*
 * One line, not a stack. It was a column because a `TitleBadge` sat above the
 * name; the roster carries **no titles at all** now, by request. Titles are
 * still on the floating nameplates over the tanks and in the kill feed, where
 * they name somebody you are about to shoot at rather than pad a scoreboard
 * that is read at a glance for K/D.
 */
.who {
  display: flex;
  align-items: baseline;
  min-width: 0;
  /* The same friendly colour the nameplate over the tank uses — a player
     reading a name here and then over a tank should not have to learn a
     second colour means the same thing. See `.plate-name` for the choice. */
  color: #4fe06a;
}

li.local {
  /* "This is you" outranks side — you already know which one you are, so the
     local row keeps the HUD's own accent rather than the ally colour. */
  color: var(--accent);
  font-weight: 600;
}

li.hostile .who {
  color: #d9483b;
  font-weight: 600;
}

.enemy-label {
  display: block;
  margin-top: 0.6rem;
}

/* Which arena this is, above the roster it describes. Quiet: it answers a
   question asked once on arrival, not one asked during a firefight. */
.arena-label {
  display: block;
  margin-bottom: 0.45rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--accent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A small, quiet marker. Which tanks are driven by an AI is worth knowing and
   not worth shouting about — it should never compete with the health bar. */
.ai {
  margin-left: 0.3rem;
  padding: 0 0.2rem;
  border: 1px solid currentColor;
  border-radius: 2px;
  font-size: 0.52rem;
  font-style: normal;
  letter-spacing: 0.06em;
  opacity: 0.6;
  vertical-align: 0.08em;
}

/*
 * A destroyed player is still in the arena, just not in it right now.
 *
 * The rule is struck through the *name* rather than the whole cell: a line
 * drawn across a chipped title reads as the title being cancelled rather than
 * the player being down, and `text-decoration` propagates into descendants
 * where a child cannot refuse it. The row still dims as a whole.
 */
li.down .who {
  opacity: 0.45;
}

li.down .who-name {
  text-decoration: line-through;
}

.score {
  font-family: ui-monospace, 'SF Mono', monospace;
  color: var(--steel-400);
}

.readout {
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.ping {
  color: var(--steel-400);
  font-family: ui-monospace, 'SF Mono', monospace;
}

.ping.bad {
  color: #d9822b;
}

/*
 * The frame counter, in the ping's own treatment.
 *
 * `min-width` so a drop from 60 to 9 does not slide the Settings button left —
 * three digits of monospace, reserved. The same amber as a bad ping, because
 * "something is wrong with what you are seeing" is one signal whichever end it
 * comes from.
 */
.fps {
  color: var(--steel-400);
  font-family: ui-monospace, 'SF Mono', monospace;
  min-width: 4.2em;
  text-align: right;
}

.fps.bad {
  color: #d9822b;
}

button {
  pointer-events: auto;
  padding: 0.35rem 0.7rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
}

button:hover {
  border-color: var(--accent);
  color: var(--accent);
}

/* ─── Health and reload ──────────────────────────────────────────────────── */

.status {
  left: 50%;
  bottom: 1.4rem;
  transform: translateX(-50%);
  width: min(420px, 60vw);
  display: grid;
  gap: 0.4rem;
}

.bar {
  position: relative;
  height: 16px;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.bar i {
  display: block;
  height: 100%;
  transition: width 90ms linear;
}

.bar.reload {
  height: 10px;
}

.bar.reload i {
  background: #5b8db8;
}

.bar.reload.loaded i {
  background: var(--accent);
}

/* Cold, so a gun storing energy does not read as one that has just gone off —
   the same signal the muzzle glow uses. */
.bar.charge {
  height: 10px;
}

.bar.charge i {
  background: #7fd4ff;
}

.bar.charge .bar-text {
  font-size: 0.52rem;
}

.bar-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
}

.bar.reload .bar-text {
  font-size: 0.52rem;
}

.tally {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/*
 * The speed sits between the two scores and is the largest thing in the row —
 * it is the only number here that changes every frame, and it moved down from
 * the top-right corner so it reads in the same glance as health and the loader
 * rather than in a second one across the screen.
 */
.tally-speed {
  flex: none;
  letter-spacing: 0;
}
.tally-speed strong {
  font-size: 1.5rem;
  line-height: 1;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
.tally-speed .unit {
  margin-left: 0.2rem;
  font-size: 0.6rem;
  color: var(--steel-400);
}

/*
 * Armour on the health bar, right-aligned against the health figure's centre.
 * A fraction of every hit rather than a pool that drains, so it is a label and
 * deliberately not a second bar. Hidden entirely at 0 — the starter hull has
 * none, and "0% ARMOUR" is noise on the one loadout that cannot change it.
 */
.bar-armour {
  position: absolute;
  top: 0;
  right: 0.4rem;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 0.55rem;
  letter-spacing: 0.08em;
  color: var(--steel-300);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

.tally strong {
  color: #fff;
}

/* ─── Kill feed ──────────────────────────────────────────────────────────── */

.kill-feed {
  position: absolute;
  top: 5.5rem;
  right: 1rem;
  display: grid;
  gap: 0.25rem;
  justify-items: end;
}

.kill {
  position: relative;
  padding: 0.25rem 0.55rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  background: rgba(11, 13, 17, 0.72);
  font-size: 0.72rem;
  animation: slide-in 160ms ease-out;
}

/*
 * **The border is the only mark on a feed line now**, and it answers the
 * question asked most often in a firefight: it is bound inline to the
 * *killer's* side, so a box outlined green means our side took a tank and a
 * red one means they took one of ours — readable from the frame alone,
 * without reading either name.
 *
 * Two other marks used to sit on this line and both are gone by request: an
 * inset accent bar down the leading edge for "involves you", and a 2px
 * killer→victim gradient under the whole line. Three marks on a line four
 * words long is more decoration than signal, and the two that went are the
 * ones whose question the *names* already answer — your own name is in the
 * line you are in, and the two names carry their sides as colour.
 *
 * `involvesLocal` is still carried on every feed entry and is no longer drawn
 * anywhere. It is left on the type rather than deleted because this HUD is
 * mid-redesign and it is the natural hook for whatever replaces the bar; if
 * that never happens, it and `pushMissionFeed`'s third argument should go.
 */

.killer,
.victim {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
}

/*
 * Colour is bound inline, from `sideColour()`, and wins over these — they are
 * only what a line with no sides (an airdrop, a round result, a mission cue)
 * falls back to. `.killer` stays the brighter of the two either way: which
 * name acted and which one was acted on is a separate signal from which side
 * either was on, so it survives being painted red or green same as it always
 * read white or steel.
 */
.killer {
  color: #fff;
}

.glyph {
  margin: 0 0.4rem;
  color: var(--accent);
}

.victim {
  color: var(--steel-400);
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
}

/* ─── Floating health bars ───────────────────────────────────────────────── */

.nameplate {
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  justify-items: center;
  gap: 2px;
  /* `will-change` matters here: these move every single frame. */
  will-change: transform;
}

/*
 * Friend or foe now lives here and nowhere else on the vehicle — the hull
 * itself is unmodulated (see `REMOTE_PALETTE` in `tank.ts`), so this colour is
 * the *only* channel a player has for a remote tank's side. That single-channel
 * bet used to be argued as a reason *against* a plain green: the friendly hue
 * was the teal `#4FE0C8` the hull wash carried, which had been measured to
 * survive a deuteranope simulation better than a green does against grass.
 * **That was overruled deliberately** — the teal read as blue to the people
 * playing it, and a side colour nobody recognises as their own is worth less
 * than one that tests better. It is `#4FE06A` now: the same luminance and
 * saturation, the hue rotated off cyan to a true green (~131°), which keeps it
 * clear of the mid-dark desaturated grass it is read against.
 *
 * `HOSTILE_TEXT` is unchanged — the exact `#D9483B` this HUD already treats as
 * "the enemy cue" everywhere else, the health bar below, `.destroyed-title`,
 * and the bound `stat-resolution.test.ts` holds every title colour away from.
 * Reusing it rather than picking a second red means that bound still measures
 * the right thing without moving a number.
 *
 * The cost of the change is real and is worth naming: green-against-red is the
 * worst axis for the ~8% of players with a red-green deficiency, and this is
 * the game's only colour channel for a remote tank's side. The non-colour
 * channels below are therefore no longer a nicety — they are what that player
 * is actually reading.
 *
 * Weight carries a second, colour-independent signal: a hostile name is bold,
 * a friendly one is not. It costs nothing and does not depend on the reader's
 * colour vision at all — on top of which the enemy's health bar is still
 * always red regardless of its value, the "Enemies" list is still its own
 * labelled section of the scoreboard, and the title above the name (when one
 * is worn) is still the same badge in both places, so a player who cannot use
 * colour at all still has three other ways to tell.
 */
.plate-name {
  font-size: 0.62rem;
  font-weight: 500;
  color: #4fe06a;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

.nameplate.hostile .plate-name {
  color: #d9483b;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
}

/*
 * The supply column's chip — the one nameplate in the game that is boxed.
 *
 * Everything else floating over a tank is deliberately unboxed: a name and a
 * bar with a hard text shadow, so eight of them over a firefight read as eight
 * labels rather than eight dialog boxes. This one takes the opposite decision
 * on purpose, and the exception is the point — there is exactly *one* of these
 * on the field, it is the thing the whole mission is about, and a player who
 * has never run an escort has to be able to tell the vehicle they are
 * protecting from the seven tanks around it at a glance. A background is the
 * cheapest way to say "this one is not like the others", and it costs nothing
 * anywhere else because no other plate can ever have it.
 *
 * Sized in `rem` and one step larger than `.plate-name`, so it holds its rank
 * over the call sign below it at every viewport — including the phone
 * landscape sizes, where the plate is the only place mission state is legible
 * at all.
 */
.plate-tag {
  display: grid;
  justify-items: center;
  gap: 1px;
  padding: 2px 7px 3px;
  margin-bottom: 1px;
  border: 1px solid var(--plate-tag-edge, rgba(255, 255, 255, 0.35));
  border-radius: 4px;
  background: rgba(8, 11, 14, 0.78);
  /* Keeps the box readable against a bright skyline as well as against grass. */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
  line-height: 1.1;
}

.plate-tag b {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #e8eef2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

/*
 * The status line, and the only part of the chip that changes.
 *
 * Three signals carry the urgency, not one: the **wording** (two of the three
 * end in an exclamation mark, one does not), the **colour**, and the chip's
 * own **border**, which brightens with it. That is the same discipline the
 * friendly/hostile plate colour is held to a few rules up — colour is the
 * game's weakest channel for the ~8% of players with a red-green deficiency,
 * so it is never the only one.
 */
.plate-tag i {
  font-size: 0.58rem;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

/* Parked on the pad, waiting for somebody to walk with it. Deliberately dim:
   nothing is wrong, and nothing needs doing beyond turning up. */
.plate-tag.is-waiting {
  --plate-tag-edge: rgba(255, 255, 255, 0.24);
}
.plate-tag.is-waiting i {
  color: #9fb0bb;
}

/* Rolling. The same green the friendly plate uses, so "good" is one colour. */
.plate-tag.is-rolling {
  --plate-tag-edge: rgba(79, 224, 106, 0.45);
}
.plate-tag.is-rolling i {
  color: #4fe06a;
}

/* Stopped for a tank in the road — a warning, not an alarm. */
.plate-tag.is-enemy-near {
  --plate-tag-edge: rgba(240, 179, 60, 0.6);
}
.plate-tag.is-enemy-near i {
  color: #f0b33c;
}

/* Being shot. The exact `#D9483B` this HUD already treats as the enemy cue
   everywhere else — the hostile plate, the health bar, `.destroyed-title` —
   so a second red is never introduced for the same meaning. */
.plate-tag.is-under-attack {
  --plate-tag-edge: rgba(217, 72, 59, 0.75);
}
.plate-tag.is-under-attack i {
  color: #d9483b;
}

.plate-bar {
  display: block;
  width: 56px;
  height: 5px;
  border: 1px solid rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.plate-bar i {
  display: block;
  height: 100%;
}

/*
 * The bar's own row, so a remote tank's HP reads as a number as well as a
 * fraction — the same pairing the local health bar has always had
 * (`.bar-text`), one size down for a plate that is 56px wide and 14 device px
 * tall. Current health only, deliberately not "142 / 230": there is no room
 * here for a second figure, and *how hurt they are relative to their own
 * tank's specific max* is exactly what the bar's fill is for — the number
 * only needs to answer "how many more hits". Coloured with `plateColour`, the
 * same function the bar's own fill uses, so the two never disagree.
 */
.plate-row {
  display: flex;
  align-items: center;
  gap: 3px;
}

.plate-hp {
  font-size: 0.56rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

/* ─── Damage, death ──────────────────────────────────────────────────────── */

.damage {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 45%, #7a1a12 100%);
  transition: opacity 90ms linear;
}

/*
 * Floating damage numbers — the opposite half of `.damage` above: that one
 * says a shell just found *you*, this says one of yours just found somebody
 * else. Anchored at screen centre exactly where `.reticle` is (same
 * `top/left: 50%`), because "rises from the reticle" is the design and the
 * impact happened at a world position the shooter is not necessarily even
 * looking at any more — projecting it would put the number off-screen or
 * behind the target as often as not. `--jitter` fans concurrent hits (a
 * pierce or a chain landing several in one tick) out sideways so they read as
 * separate numbers rather than one smear.
 *
 * The rise-and-fade is a CSS animation triggered on mount, not a per-frame
 * style binding: `combat.hitMarkers` is rebuilt every rendered frame like
 * every other `HudState` field, but Vue patches the existing node by `key`
 * rather than remounting it, so the animation plays through untouched. See
 * `HIT_MARKER_LIFETIME_MS` in `arena-scene.ts` — this duration and that one
 * must agree, or the number either snaps away mid-fade or lingers invisibly.
 */
.hit-markers {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
}

.hit-marker {
  position: absolute;
  left: 0;
  top: -2.9rem;
  transform: translate(calc(-50% + var(--jitter, 0rem)), 0);
  font-size: 1.3rem;
  font-weight: 700;
  color: #ff4433;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  animation: hit-marker-rise 1.5s ease-out forwards;
}

@keyframes hit-marker-rise {
  0% {
    opacity: 1;
    color: #ff4433;
    transform: translate(calc(-50% + var(--jitter, 0rem)), 0);
  }
  33% {
    opacity: 1;
    color: #ff4433;
    transform: translate(calc(-50% + var(--jitter, 0rem)), -0.6rem);
  }
  100% {
    opacity: 0;
    color: #1a0605;
    transform: translate(calc(-50% + var(--jitter, 0rem)), -3rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hit-marker {
    /* Show-then-vanish rather than show-then-fly: the number holds fully
       visible at its resting position for the whole 1.5s and disappears the
       instant `pruneHitMarkers` drops it, with no motion and no colour
       sweep. */
    animation: none;
    opacity: 1;
    color: #ff4433;
  }
}

.destroyed {
  position: absolute;
  inset: 0;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 0.5rem;
  background: rgba(11, 13, 17, 0.45);
}

/* ─── The round banner ───────────────────────────────────────────────────────
 *
 * The result screen's banner language — a full-width band, a headline between
 * rules that fade toward the edges, colour carried by the word and the rules
 * together — at a round's own weight, one size down from the battle result so
 * the two read as a family with the battle the louder of them. Deliberately
 * still translucent: a round ending is the loudest thing that happens *without*
 * the battle ending, and there is a live arena a second behind it. The band's
 * dark centre is what lets the type grow without the score becoming unreadable
 * over grass — the job the old boxed plate did, minus the dialog-box look.
 */
.round-end {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  background: rgba(11, 13, 17, 0.45);
}

.round-banner {
  position: relative;
  width: 100%;
  display: grid;
  justify-items: center;
  gap: clamp(0.4rem, 1.4vh, 0.7rem);
  padding: clamp(1rem, 4vh, 2rem) 1.2rem;
  text-align: center;
  /* The band: solid enough in the middle to carry type over grass, gone at its
     own top and bottom so it reads as a stripe across the battle rather than a
     box on top of it. */
  background: linear-gradient(
    180deg,
    rgba(9, 11, 15, 0) 0%,
    rgba(9, 11, 15, 0.78) 22%,
    rgba(9, 11, 15, 0.78) 78%,
    rgba(9, 11, 15, 0) 100%
  );
}

/* A faint tone-coloured glow behind the headline — the result screen's ember
   radial, in the round's own colour. On an overlay element so the band gradient
   is stated once, not once per tone. */
.round-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.round-banner.won::before {
  background: radial-gradient(52% 130% at 50% 50%, rgba(79, 224, 106, 0.12), rgba(0, 0, 0, 0) 70%);
}

.round-banner.lost::before {
  background: radial-gradient(52% 130% at 50% 50%, rgba(217, 72, 59, 0.13), rgba(0, 0, 0, 0) 70%);
}

.round-banner.counting::before {
  background: radial-gradient(52% 130% at 50% 50%, rgba(232, 114, 44, 0.1), rgba(0, 0, 0, 0) 70%);
}

/* The last two seconds of the count-in: the same orange, turned up — a fuller
   glow than the eight seconds it follows, because this is the payoff rather
   than the wait. */
.round-banner.imminent::before {
  background: radial-gradient(58% 150% at 50% 50%, rgba(232, 114, 44, 0.4), rgba(0, 0, 0, 0) 75%);
}

/* Children above the glow. */
.round-banner > * {
  position: relative;
}

.round-eyebrow {
  font-size: 0.66rem;
  letter-spacing: 0.34em;
  text-indent: 0.34em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/* The headline between fading rules — `.result-title`'s construction one size
   down. The near-word end of each rule takes the tone colour, so the result is
   carried by the rules and the word together, never by the word alone: the
   same reason the kill feed pairs a colour with a weight. */
.round-headline {
  width: min(880px, 94vw);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1.3rem;
  font-size: min(clamp(2.2rem, 6vw, 3.8rem), max(1.6rem, 8vh));
  font-weight: 800;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  line-height: 1;
  color: var(--steel-100);
  text-shadow: 0 2px 22px rgba(0, 0, 0, 0.85);
}

.round-headline::before,
.round-headline::after {
  content: '';
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0), var(--steel-500));
}

.round-headline::after {
  background: linear-gradient(270deg, rgba(0, 0, 0, 0), var(--steel-500));
}

/* Tracking trails the last glyph; pull the word back one unit so it sits
   optically centred between the rules — `.result-word`'s own correction. */
.round-headline-word {
  font-weight: inherit;
  margin-right: -0.26em;
  white-space: nowrap;
}

.round-banner.won .round-headline-word {
  color: #4fe06a;
}
.round-banner.won .round-headline::before {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(79, 224, 106, 0.6));
}
.round-banner.won .round-headline::after {
  background: linear-gradient(270deg, rgba(0, 0, 0, 0), rgba(79, 224, 106, 0.6));
}

.round-banner.lost .round-headline-word {
  color: #d9483b;
}
.round-banner.lost .round-headline::before {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(217, 72, 59, 0.6));
}
.round-banner.lost .round-headline::after {
  background: linear-gradient(270deg, rgba(0, 0, 0, 0), rgba(217, 72, 59, 0.6));
}

.round-banner.counting .round-headline::before {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(232, 114, 44, 0.55));
}
.round-banner.counting .round-headline::after {
  background: linear-gradient(270deg, rgba(0, 0, 0, 0), rgba(232, 114, 44, 0.55));
}

/* The count-in's own numeral: the biggest single glyph in the game, because it
   is the only thing on screen that matters for ten seconds. It overrides the
   headline's size, not its construction — the rules flank it the same way. */
.round-count {
  font-size: min(clamp(4rem, 13vw, 8rem), max(2.6rem, 16vh));
  line-height: 0.9;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  margin-right: 0;
  color: var(--accent);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.9);
}

.round-stage {
  font-size: clamp(1rem, 2.6vw, 1.5rem);
  letter-spacing: 0.3em;
  text-indent: 0.3em;
  text-transform: uppercase;
  color: var(--steel-100);
}

/* The beat the count-in ends on — no rules, no eyebrow, nothing left to plan.
   At least as large as the numeral it replaces, because it is the moment the
   whole ten seconds was building to. Its own entrance rather than the
   banner's shared fade: a scale-in reads as an arrival, which a beat needs
   and a countdown number does not. */
.to-battle-text {
  font-size: min(clamp(3.4rem, 12vw, 8rem), max(2.6rem, 16vh));
  font-weight: 900;
  letter-spacing: 0.16em;
  text-indent: 0.16em;
  text-transform: uppercase;
  line-height: 0.9;
  color: var(--accent);
  text-shadow:
    0 0 46px rgba(232, 114, 44, 0.6),
    0 4px 24px rgba(0, 0, 0, 0.9);
}

/* Beats `.round-banner > *`'s shared fade on specificity, not source order —
   that rule sits after this one in the file. */
.round-banner.imminent .to-battle-text {
  animation: to-battle-in 0.35s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

@keyframes to-battle-in {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ── The tally: pips and numerals ──
 * One cell per round to win, per side. Ours fill outward to the left, theirs
 * to the right (`row-reverse` on ours), so the outer edge is the match and the
 * unlit run is the runway either side has left. */
.round-tally {
  display: flex;
  align-items: center;
  gap: clamp(0.6rem, 2vw, 1.1rem);
}

.pips {
  display: flex;
  gap: 0.3rem;
}

.pips.mine {
  flex-direction: row-reverse;
}

.pips i {
  width: clamp(0.55rem, 1.8vw, 0.85rem);
  height: 0.34rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid var(--steel-600);
  transform: skewX(-14deg);
}

.pips.mine i.lit {
  background: #4fe06a;
  border-color: #4fe06a;
  box-shadow: 0 0 8px rgba(79, 224, 106, 0.5);
}

.pips.theirs i.lit {
  background: #d9483b;
  border-color: #d9483b;
  box-shadow: 0 0 8px rgba(217, 72, 59, 0.5);
}

/* The pip this round just lit lands with a flash — opacity/transform only, on
   the compositor, and it runs once. The skew is restated because `transform`
   is one property: the keyframe would otherwise unskew the cell it animates. */
@keyframes pip-in {
  from {
    opacity: 0;
    transform: skewX(-14deg) scale(2.1);
  }
  to {
    opacity: 1;
    transform: skewX(-14deg) scale(1);
  }
}

.pips i.fresh {
  animation: pip-in 0.5s cubic-bezier(0.2, 0.7, 0.3, 1) 0.25s both;
}

.tally-score {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  color: var(--steel-100);
}

.tally-score em {
  font-style: normal;
  font-weight: 400;
  font-size: 0.8em;
  color: var(--steel-400);
}

/* Match point, said in words on the one screen the player is reading rather
   than fighting. Amber, because it is a warning about the match rather than a
   result — the result already has its colour one row up. */
.round-alert {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-indent: 0.22em;
  text-transform: uppercase;
  color: var(--accent);
}

.round-note {
  font-size: 0.9rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-300);
}

.round-note b {
  font-size: 1.15rem;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
}

/* The result screen's staggered entrance, faster — this banner has five
   seconds of life, not a debrief's. Values land once; nothing loops. */
@keyframes round-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.round-banner > * {
  animation: round-rise 0.3s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

.round-banner > :nth-child(2) {
  animation-delay: 0.06s;
}

.round-banner > :nth-child(3) {
  animation-delay: 0.12s;
}

.round-banner > :nth-child(4) {
  animation-delay: 0.18s;
}

.round-banner > :nth-child(5) {
  animation-delay: 0.24s;
}

@media (prefers-reduced-motion: reduce) {
  .round-banner > *,
  .round-banner.imminent .to-battle-text,
  .pips i.fresh {
    animation: none;
  }
}

/* What leaving costs, under the escape menu's exit. */
.menu-cost {
  margin: 0.55rem 0 0;
  font-size: 0.62rem;
  line-height: 1.5;
  letter-spacing: 0.04em;
  color: #d9483b;
}

/* ─── The forfeit window ─────────────────────────────────────────────────────
 *
 * Reuses `.result`'s scrim and column, because it *is* a result screen — just for
 * a battle the player ended themselves. Only the figures are its own.
 */
.forfeit-figures {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.forfeit-plate {
  display: grid;
  justify-items: center;
  gap: 0.2rem;
  min-width: 8.5rem;
  padding: 0.9rem 1.4rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  background: rgba(20, 23, 30, 0.75);
}

.forfeit-plate b {
  font-size: 1.7rem;
  line-height: 1;
  color: var(--steel-100);
}

.forfeit-plate span {
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.forfeit-note {
  max-width: 34rem;
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.6;
  text-align: center;
  color: #d9483b;
}

.destroyed-title {
  font-size: 2.2rem;
  letter-spacing: 0.3em;
  color: #d9483b;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.8);
}

.destroyed-sub {
  font-size: 0.8rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-200);
}

/*
 * Its own class, deliberately not folded into `.destroyed-sub`: the two are
 * shared with the intermission overlay (`hud.intermissionSeconds`, above),
 * and this line only ever exists on a death — inlining it there would give a
 * round-lost banner a `strong` selector with nothing to colour.
 */
.destroyed-killer {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  color: var(--steel-200);
}

.destroyed-killer strong {
  font-weight: 600;
}

/* ─── The result screen ──────────────────────────────────────────────────────
 *
 * Full screen, and near-opaque on purpose: the frozen battlefield ghosts
 * through as atmosphere, never as something that still looks playable. It owns
 * the scroll — on a short viewport the debrief scrolls as one column rather
 * than growing inner scrollbars — and the `margin: auto` on the column is what
 * centres it when it fits without clipping the top when it does not.
 */
/*
 * The debrief, over a painting of the result.
 *
 * ## Three paintings, and the split is the palette's own
 *
 * `/assets/result/{victory,loss,left}.webp` — 1920x1080, the loading screen's
 * format and its blue-hour register, so the screen a sortie ends on belongs to
 * the same key art it began on. What tells them apart is the relationship the
 * whole product is built from: **victory is amber-dominant** (tanks holding
 * open ground, sunlit) and **defeat is cold-dominant** (a broken hull sunk in
 * mud, the fight still burning behind it). A player reads warm-against-cold
 * before they read a word, which is the same argument `--bone` against
 * `--amber` makes on the landing page.
 *
 * **`victory.webp` is not `win.webp`.** They were the same file until the
 * defeat/win paintings both grew a video loop; `win.{webp,mp4,webm}` is also
 * the landing page's own `#ground` art (`LandingScreen.vue`, deliberate
 * reuse — see its own comment), and swapping its footage for this screen's
 * would have changed a marketing section as a side effect of a result-screen
 * request. `victory.*` is this screen's own copy instead, so the two can move
 * independently.
 *
 * `left.webp` is the abandoned screen's: an empty field at grey twilight with
 * one set of track ruts running out of frame and the fight still burning on the
 * horizon. Deliberately unheroic and deliberately *empty* — the subject of that
 * screen is a battle nobody is in any more.
 *
 * ## A draw takes the defeat painting, on purpose
 *
 * The banner already says DRAW, and a draw is emphatically not a *departure*, so
 * `left.webp` would be a picture of something that did not happen. What is left
 * is a fourth painting for a state that needs equal-total kills or a mutual
 * wipe-out to reach, spent on a distinction the player reads in the word. So the
 * cold painting carries "the fight is over and you did not take it", which is
 * what a draw is.
 *
 * ## The scrim is the load-bearing half
 *
 * This column is centred and its text runs down the middle of the frame, unlike
 * the loading screen's bottom-left plate — so the scrim is heaviest in the
 * middle rather than at the bottom, and every painting was composed with an
 * open, low-contrast centre to meet it. The ember radial at the top is the one
 * that was already here and is unchanged: it is what ties the banner into the
 * payout ribbon below it.
 *
 * **`backdrop-filter` is gone with the transparency, and that is a saving.** The
 * scrim used to sit at 0.93-0.975 alpha over a 7px blur of the frozen arena —
 * so the blur was paying for a full-screen filter pass to be 3% visible. The
 * painting is opaque, so there is nothing behind this to blur.
 *
 * A missing file degrades to the flat scrim over the arena, which is exactly the
 * screen this replaced.
 */
.result {
  position: absolute;
  inset: 0;
  /*
   * Above the settings window's 60, which is what this number is for: the
   * forfeit receipt is `.result forfeit`, and pressing *Leave battle* does not
   * close the menu — `requestLeave` suspends the input source and awaits a
   * settle, then App clears its own `menuOpen` and not this component's. That
   * was invisible while the menu carried no z-index and simply lost to whatever
   * came later in the tree; once the window had one, the receipt appeared
   * *behind* it and its *Return to garage* was unreachable, which is a player
   * stuck in a battle they have already left. `e2e/harness/session.mjs`'s
   * `leaveBattle` is what caught it.
   *
   * The other half of that ordering is deliberate and unchanged: Escape over a
   * decided battle still opens a window nobody can see, exactly as it did
   * before. The result screen is the terminal statement and has its own exit;
   * this is not the commit to start layering things over it.
   */
  z-index: 70;
  overflow-y: auto;
  pointer-events: auto;
  display: flex;
  background-color: var(--steel-900);
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(46, 26, 12, 0.5), rgba(0, 0, 0, 0) 60%),
    linear-gradient(180deg, rgba(9, 11, 15, 0.82), rgba(9, 11, 15, 0.68) 42%, rgba(9, 11, 15, 0.93)),
    url('/assets/result/loss.webp');
  background-size: auto, auto, cover;
  background-position: center, center, center;
  background-repeat: no-repeat;
}

/*
 * Warm light wants a fractionally lighter scrim to survive, and the ember radial
 * is redundant over a dawn — the painting is already carrying that colour.
 */
.result.won {
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(46, 26, 12, 0.32), rgba(0, 0, 0, 0) 62%),
    linear-gradient(180deg, rgba(9, 11, 15, 0.78), rgba(9, 11, 15, 0.64) 42%, rgba(9, 11, 15, 0.92)),
    url('/assets/result/victory.webp');
}

/* The abandoned screen. Flatter and colder than either result: the painting has
   no fire in the foreground to warm it, and nothing here is being celebrated. */
.result.forfeit {
  background-image:
    linear-gradient(180deg, rgba(9, 11, 15, 0.8), rgba(9, 11, 15, 0.66) 42%, rgba(9, 11, 15, 0.93)),
    url('/assets/result/left.webp');
  background-size: auto, cover;
}

/*
 * The footage over either painting — `resultVideoSources` in the script
 * picks `victory.*` or `loss.*` off the same `hud.won` the CSS already keys
 * `.won` on. `.result`'s own background (the two gradients plus the still)
 * is untouched and stays underneath — this is a pure overlay, opacity 0
 * until `@playing` says there is something to show.
 */
.result-video {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1.1s ease;
}

.result-video.ready {
  opacity: 1;
}

/* The same two gradients `.result`'s own background carries, mirrored onto
   a layer that sits above the video — the scrim has to be repainted here or
   the footage would cover it, and the debrief text needs it whichever of
   the still or the video is actually on screen. */
.result-scrim {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(46, 26, 12, 0.5), rgba(0, 0, 0, 0) 60%),
    linear-gradient(180deg, rgba(9, 11, 15, 0.82), rgba(9, 11, 15, 0.68) 42%, rgba(9, 11, 15, 0.93));
}

/* `.result.won`'s own lighter gradient, mirrored the same way `.result-scrim`
   mirrors `.result`'s — so the video reads as warm rather than through the
   defeat screen's colder scrim. */
.result.won .result-scrim {
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(46, 26, 12, 0.32), rgba(0, 0, 0, 0) 62%),
    linear-gradient(180deg, rgba(9, 11, 15, 0.78), rgba(9, 11, 15, 0.64) 42%, rgba(9, 11, 15, 0.92));
}

.result-col {
  position: relative;
  z-index: 2;
  margin: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  /*
   * The debrief must fit one screen on every device — the owner's rule, and
   * `.result`'s `overflow-y: auto` is the safety net rather than the design.
   * Every vertical in this column therefore breathes with the viewport's
   * *height*: the 1.6rem/2.8rem rhythm is the ceiling a tall desktop shows,
   * and a 660px laptop window compresses toward the floors instead of pushing
   * the Return button off the screen (measured before this: 895px of column,
   * 235px of scroll debt at 1280×660, and 75px on a Mac 13"). The same
   * pressure runs through `BattleSummary.vue`'s own verticals.
   */
  gap: clamp(0.65rem, 2vh, 1.6rem);
  padding: clamp(0.8rem, 3.3vh, 2.8rem) 0 clamp(0.7rem, 2.8vh, 2.4rem);
  /*
   * The second half of the one-screen rule: the column may never outgrow the
   * viewport, and the one child allowed to give way is the scoreboard's own
   * rows — `BattleSummary` carries the rest of this flex chain (`min-height: 0`
   * down to `.board-rows`, which scrolls within itself). A Karsten Basin
   * roster is fifteen a side, and thirty rows fit no screen at any type size;
   * capping them at a viewport fraction was wrong twice over — too loose on a
   * short laptop and biting on a desktop with room to spare — where "whatever
   * is left" is right at every height by construction.
   */
  max-height: 100%;
}

/* The banner and the exit never give way; the summary is the shrinkable one.
   (`.summary` is the child component's root, which carries this scope.) */
.result-col > .result-head,
.result-col > .result-leave,
.result-col > .result-actions {
  flex: none;
}

/* The exit, and on touch the fuel button beside it. */
.result-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
}

.result-col > .summary {
  min-height: 0;
}

/* One orchestrated entrance: banner, then the body, then the way out. The
   values land once and never move again — nothing here loops. */
@keyframes result-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.result-col > * {
  animation: result-rise 0.45s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

.result-col > :nth-child(2) {
  animation-delay: 0.12s;
}

.result-col > :nth-child(3) {
  animation-delay: 0.24s;
}

@media (prefers-reduced-motion: reduce) {
  .result-col > * {
    animation: none;
  }
}

.result-head {
  width: min(1060px, 92vw);
  display: grid;
  justify-items: center;
  gap: clamp(0.25rem, 0.8vh, 0.6rem);
}

.result-eyebrow {
  font-size: 0.66rem;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/*
 * The row the headline sits on. A plain flex wrap rather than a grid, because
 * the flanking scores (`.result-title-score`) are optional siblings of
 * `.result-title` now, not part of its own rule/word/rule grid — `v-if`'d out
 * entirely for a mission or a draw, where there is no numeric pair to show.
 *
 * Only the *decided-battle* screen wraps `.result-title` in this row — the
 * forfeit screen's own `<h2 class="result-title lost">` sits straight in
 * `.result-head`'s grid with no row around it, so the override below that
 * lets `.result-title` shrink to share space with the flanking scores is
 * scoped to `.result-title-row .result-title` rather than put on the bare
 * class. Widening that scope would leave the forfeit headline's fading rules
 * with no row to stretch across.
 */
.result-title-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(0.7rem, 2.4vw, 1.8rem);
}

/* `.result-title` takes the full row on its own — matching its old
   `width: 100%` — until a flanking score is present to share it with. */
.result-title-row .result-title {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
}

/* The headline, flanked by rules that fade toward the edges — the banner is a
   thesis, and the rules are what make one word read as a full-width statement. */
.result-title {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1.6rem;
  /* Enlarged with the round popup, so the two read as one family with the battle
     result the louder of them: 4.2rem -> 5.4rem at the top of the clamp. The
     `min()` against a height clamp is the one-screen rule: a short window caps
     the headline by what it can afford vertically (8.5vh is 56px at 660, 32px
     on a landscape phone) without touching what a tall desktop shows. */
  font-size: min(clamp(2.6rem, 7.5vw, 5.4rem), max(1.7rem, 8.5vh));
  font-weight: 800;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #d9483b;
  text-shadow: 0 2px 26px rgba(0, 0, 0, 0.8);
}

/*
 * The rounds/kills pair flanking the word — "X · VICTORY · Y" — coloured the
 * same green/red as the roster, the kill feed and the floating nameplates, so
 * nothing new has to be learnt to read it. Sized against the same headline
 * clamp family as `.result-title` (a fraction of it, `flex: none` so it never
 * competes with the word for the row's width) rather than against
 * `.result-score`'s old, much smaller scale — this is the second-loudest
 * thing on the screen now, not a footnote under it.
 */
.result-title-score {
  flex: none;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-size: min(clamp(1.9rem, 5.6vw, 3.8rem), max(1.3rem, 6.5vh));
  text-shadow: 0 2px 22px rgba(0, 0, 0, 0.75);
}

.result-title-score.us {
  color: #4fe06a;
}

.result-title-score.them {
  color: #d9483b;
}

.result-title::before,
.result-title::after {
  content: '';
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0), var(--steel-600));
}

.result-title::after {
  background: linear-gradient(270deg, rgba(0, 0, 0, 0), var(--steel-600));
}

/* No `margin-right` compensation: this used to pull the word back by one
   tracking unit on the assumption that `letter-spacing` trails the last
   glyph. It doesn't, in the engine this game ships on — the word sat exactly
   centred with the correction removed and 10.7px right of centre with it in,
   measured against `.result-head`'s own box. Re-add only after confirming the
   rendering engine actually adds trailing letter-spacing; don't restore it on
   theory alone. */
.result-word {
  font-weight: inherit;
  white-space: nowrap;
}

.result-title.won {
  color: #4caf6d;
}

.result-title.draw {
  color: var(--steel-200);
}

.result-sub {
  font-size: 0.8rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel-200);
}

/*
 * Reason and unit on one baseline row, wrapping only if it truly cannot fit.
 * The figures themselves moved up onto the headline row — see
 * `.result-title-score` — so what is left here is only the reason it ended
 * and, when there is a numeric pair to read, what it was counted in.
 */
.result-line {
  margin: 0;
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.1rem 0.75rem;
}

/* Says which currency the headline's pair is in — kills or rounds — without
   repeating the figures. Sized right down: it is a unit, not a figure. */
.result-score-unit {
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/* The way off the result screen. The cursor is back by the time this exists —
   the input source is suspended when the battle is decided — so a button is
   usable here in a way it is not anywhere else on the HUD. */
.result-leave {
  padding: clamp(0.55rem, 1.2vh, 0.8rem) 2.6rem;
  border: 1px solid rgba(232, 114, 44, 0.75);
  border-radius: 3px;
  background: linear-gradient(180deg, rgba(232, 114, 44, 0.28), rgba(232, 114, 44, 0.12));
  color: #ffc98a;
  font: inherit;
  font-size: 0.8rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
}

.result-leave:hover,
.result-leave:focus-visible {
  background: linear-gradient(180deg, rgba(232, 114, 44, 0.45), rgba(232, 114, 44, 0.22));
  color: #fff;
  box-shadow: 0 0 24px rgba(232, 114, 44, 0.25);
}

/* ─── Settings and the escape menu ──────────────────────────────────────── */

/*
 * The window itself is `ui/SettingsPanel.vue`, which owns its own scrim, card,
 * groups and controls — everything that used to be restated here. What is left
 * is the arena's own footer, rendered into that window's `actions` slot: slot
 * content carries *this* component's scope id, so these rules reach it and the
 * panel's own cannot.
 *
 * There used to be a full copy of `.setting`, `.setting-name`, `.setting-value`,
 * `.setting-hint` and the range styling in this file, left over from the days
 * when the panel was teleported into the menu card. None of it did anything:
 * scoped CSS stamps the scope attribute on the *last* selector, and those
 * elements live inside the child, so every rule but the ones matching the
 * child's root node was dead. Dead CSS that looks live is worse than none —
 * it is where the next person tunes a number and sees nothing change.
 */

.settings-toggle.active {
  border-color: rgba(232, 114, 44, 0.7);
  color: #e8722c;
}

/* Stated plainly, because a menu that looks like a pause and is not one gets
   players killed while they read it. */
.menu-sub {
  margin: 0 0 0.7rem;
  color: var(--steel-400);
  font-size: 0.7rem;
  line-height: 1.45;
}

.menu-buttons {
  display: grid;
  /* Side by side where there is room for both — a landscape phone has width and
     nothing else — and stacked where there is not. */
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.5rem;
}

/*
 * Both are *deltas* on the shared button look `SettingsPanel` gives everything
 * in its footer, so only the colour is stated here. The two-class selectors are
 * deliberate: the panel's own rule is `.win-actions[data-v-…] button`, which
 * carries one more class-or-attribute than a bare `.menu-resume[data-v-…]` and
 * would otherwise win the border back.
 */
.menu-buttons .menu-resume {
  border-color: var(--accent);
  background: rgba(232, 114, 44, 0.85);
  color: #14171c;
  font-weight: 600;
}

.menu-buttons .menu-resume:hover {
  background: var(--accent);
  color: #14171c;
}

.menu-buttons .menu-leave {
  border-color: rgba(217, 72, 59, 0.6);
  color: #d9483b;
}

.menu-buttons .menu-leave:hover {
  border-color: #d9483b;
  background: rgba(217, 72, 59, 0.14);
  color: #fff;
}

/* ─── Tutorial ─────────────────────────────────────────────────────────── */

/*
 * Top centre, and narrow.
 *
 * Two constraints shape it. It must not reach the middle of the screen, which
 * is the gunsight and everything the player is aiming at — hence the cap on
 * width and the fact that it hangs from the top edge rather than being centred
 * vertically. And it must never intercept the pointer: the arena is dragged to
 * aim, and a panel that swallowed a drag would stop the turret dead over its own
 * width. `pointer-events: none` on the whole thing is safe precisely because
 * nothing in here is clickable — the controls are in the escape menu.
 */
.tutorial {
  position: absolute;
  top: 0.9rem;
  left: 50%;
  transform: translateX(-50%);
  width: min(26rem, calc(100vw - 2rem));
  padding: 0.65rem 0.85rem 0.55rem;
  border: 1px solid var(--steel-600);
  border-left: 2px solid var(--accent, #e8722c);
  border-radius: 3px;
  background: rgba(11, 13, 17, 0.82);
  backdrop-filter: blur(4px);
  pointer-events: none;
}

.tutorial-step {
  margin: 0;
  color: var(--accent, #e8722c);
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.tutorial-title {
  margin: 0.15rem 0 0.2rem;
  font-size: 1rem;
  font-weight: 600;
}

.tutorial-blurb {
  margin: 0;
  color: var(--steel-400);
  font-size: 0.72rem;
  line-height: 1.4;
}

.tutorial-goals {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.2rem;
}

/*
 * Three columns: the tick, the instruction, the count. The count is its own
 * column and right-aligned so the numbers line up down the panel — a progress
 * readout that jitters horizontally as `9 / 25` becomes `10 / 25` reads as the
 * panel twitching rather than as progress.
 */
.tutorial-goal {
  display: grid;
  /* Four cells now, and the caps take the whole second row across the two middle
     columns: a cap is 1.35 rem tall against a 0.76 rem line, so inline it would
     set the row height and the ticks would stop lining up with the labels. */
  grid-template-columns: 1rem 1fr auto;
  grid-template-areas:
    'tick label count'
    '.    keys  keys';
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.76rem;
  /* Unfinished goals are the ones to read, so a finished one steps back rather
     than lighting up. The tick is the signal; the dimming is the reward. */
  color: var(--steel-200, #d7dde3);
}

.tutorial-goal.done {
  color: var(--steel-400);
  text-decoration: line-through;
  text-decoration-color: rgba(255, 255, 255, 0.25);
}

.tutorial-tick {
  text-align: center;
  /* `steel-400`, not `steel-600`: on this panel's dark plate `steel-600` fell to
     almost nothing (1.5:1) — same fix as the loading screen's `.or` for the same
     reason. */
  color: var(--steel-400);
}

.tutorial-goal.done .tutorial-tick {
  color: #4caf6d;
  text-decoration: none;
}

.tutorial-tick {
  grid-area: tick;
}

.tutorial-label {
  grid-area: label;
}

.tutorial-count {
  grid-area: count;
  color: var(--steel-400);
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

/* ─── Key caps ─────────────────────────────────────────────────────────── */

/*
 * Which buttons to press, drawn rather than described.
 *
 * The row above says *what* to do and never names a control; these say which
 * one, and they are the brightest thing in the panel while the drill is
 * unfinished. That ordering is the design: a player who has understood the
 * sentence needs the key, and a player who has not is reading the sentence
 * anyway.
 */
.tutorial-keys {
  grid-area: keys;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.15rem;
}

.tutorial-or {
  /* `steel-400`, not `steel-600` — see `.tutorial-tick`. */
  color: var(--steel-400);
  font-size: 0.6rem;
  letter-spacing: 0.06em;
}

/*
 * The cap itself is `ui/KeyCap.vue` now — drawing, lip, mouse glyph and pulse all
 * moved there so the loading screen could show the same controls without a second
 * set of caps to keep in step. What stays here is only how the *row* of them sits,
 * and the one thing the cap cannot know — whether its *goal* is finished.
 *
 * The reduced-motion override went with it rather than being left here as well:
 * two places deciding one animation is how they come to disagree.
 */

/* A satisfied goal's caps step back with the rest of the row. Reaches into the
   child's root element, which carries this component's scope id — the state is
   the goal's, not the cap's, so it cannot live in `KeyCap`. */
.tutorial-goal.done .keycap {
  border-color: var(--steel-600);
  background: transparent;
  color: var(--steel-400);
  text-decoration: none;
}

.tutorial-note {
  margin: 0.5rem 0 0;
  padding-left: 0.5rem;
  border-left: 2px solid var(--steel-600);
  color: var(--steel-400);
  font-size: 0.68rem;
  line-height: 1.4;
}

.tutorial-hint {
  margin: 0.55rem 0 0;
  /* `steel-200`, not `steel-400`: at 0.62rem on this panel's translucent plate
     `steel-400` still only reaches ~4.1:1, and this line is the only way a new
     player learns how to leave training — it has to be unambiguously legible. */
  color: var(--steel-200);
  font-size: 0.62rem;
  letter-spacing: 0.04em;
}

.tutorial-hint kbd {
  padding: 0 0.25rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.6rem;
}

/* Set to the line it sits in, so the hint stays one line rather than growing a
   row around an icon. `GearIcon` states no size of its own — the caller does —
   and `GearIcon` sets its own `display: block` (so it never squashes inside a
   flex row), which put the glyph on a line of its own above the words here. */
.tutorial-hint-gear {
  display: inline-block;
  width: 0.8rem;
  height: 0.8rem;
  vertical-align: -0.16em;
}

.menu-tutorial {
  /* Sits above Resume/Leave with its own gap, so "what to do with the lesson"
     and "what to do with the battle" do not read as one list of four. */
  margin-top: 1rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid var(--steel-700, rgba(255, 255, 255, 0.08));
}

.menu-secondary {
  color: var(--steel-200, #d7dde3);
}

.menu-secondary:hover {
  border-color: var(--accent, #e8722c);
  color: var(--accent, #e8722c);
}

/* ─── Gunsight ─────────────────────────────────────────────────────────── */

.reticle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5.5rem;
  height: 5.5rem;
  transform: translate(-50%, -50%);
  /* Never intercepts the mouse: the whole screen has to stay draggable. */
  pointer-events: none;
}

.reticle svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.reticle line {
  stroke: rgba(255, 255, 255, 0.55);
  stroke-width: 1.6;
  /* Readable over a pale building as well as over dark ground. */
  filter: drop-shadow(0 0 1.5px rgba(0, 0, 0, 0.9));
}

.ring-track {
  fill: none;
  stroke: rgba(255, 255, 255, 0.14);
  stroke-width: 2;
}

.ring {
  fill: none;
  stroke: rgba(255, 255, 255, 0.5);
  stroke-width: 2;
}

.pip {
  fill: rgba(255, 255, 255, 0.75);
  filter: drop-shadow(0 0 1.5px rgba(0, 0, 0, 0.9));
}

/* ─── The objective marker ─────────────────────────────────────────────── */

/*
 * Where to go. Amber rather than the accent, because the accent is the gun and
 * the reload and an objective is not a statement about the gun — one constant,
 * `OBJECTIVE_COLOUR`, shared with the ring drawn on the ground so the two cannot
 * disagree about which thing is the objective.
 *
 * `pointer-events: none` like everything else out here: aiming holds pointer
 * lock, so a HUD element that swallowed a drag would stop the turret dead over
 * its own width.
 */
.objective {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  transform: translate(-50%, -50%);
  color: #f2c14e;
  pointer-events: none;
  /* Above the nameplates, below the panels: it is the thing being looked for. */
  z-index: 6;
}

.objective-glyph {
  width: 2.1rem;
  height: 2.1rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.85));
}

/* The arrival glyph is hidden while there is somewhere to go, and the chevron
   while there is not. Two glyphs in one element — see the template. */
.objective .ring,
.objective .tick {
  display: none;
}

.objective.reached .chev {
  display: none;
}

.objective.reached .ring,
.objective.reached .tick {
  display: initial;
}

.objective.reached {
  color: #4caf6d;
}

/*
 * Off screen: the chevron turns to point the way out of the frame.
 *
 * A rotation on the glyph rather than a different path, so the arrow the player
 * has been following is visibly the same arrow when it reaches the edge.
 */
.objective.clamped.left .objective-glyph {
  transform: rotate(90deg);
}

.objective.clamped.right .objective-glyph {
  transform: rotate(-90deg);
}

/*
 * The pulse says "not on screen", and it is on **opacity only**.
 *
 * Never on transform or size: this element is positioned every frame from a
 * bearing, and an animated transform would fight the rotation above while a
 * changing size would make the label jump. Same rule the tutorial's key caps
 * follow.
 */
@keyframes objective-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

.objective.clamped {
  animation: objective-pulse 1.1s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .objective.clamped {
    animation: none;
  }
}

.objective-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

/* The gun is still coming round to the view: the sight thins out so the gun
   marker — where a shot would actually go — is the brighter of the two. */
.reticle.slewing line {
  stroke: rgba(255, 255, 255, 0.3);
}

.reticle.slewing .pip {
  fill: rgba(255, 255, 255, 0.4);
}

.gun-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

/* A diamond rather than a second ring, so the two marks cannot be mistaken
   for one another at a glance: the ring is the view, the diamond is the gun. */
.gun-marker .diamond {
  display: block;
  width: 0.65rem;
  height: 0.65rem;
  border: 2px solid rgba(232, 114, 44, 0.95);
  transform: rotate(45deg);
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9));
}

/* Pinned to the edge: the gun is more than a screenful away. */
.gun-marker.clamped .diamond {
  animation: gun-marker-pulse 0.6s ease-in-out infinite alternate;
}

@keyframes gun-marker-pulse {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.35;
  }
}

/*
 * The true aim: a real 3-D projection of the barrel's level firing line, so
 * it moves both axes rather than the gun marker's horizontal-only estimate.
 * A cross rather than the diamond above it — same rule, a shape nobody could
 * mistake for the sight's own ring or for the gun marker's yaw-lag diamond.
 */
.aim-reticle {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 1.3rem;
  height: 1.3rem;
  pointer-events: none;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9));
}

.aim-reticle line {
  stroke: rgba(232, 114, 44, 0.95);
  stroke-width: 2;
  stroke-linecap: round;
}

/* Pinned to the edge rather than sitting where the level line actually
   projects — dimmer, so it reads as "level is this way" and not as a claim
   that the shot lands exactly here. */
.aim-reticle.clamped line {
  stroke: rgba(232, 114, 44, 0.55);
}

/* Loaded: the ring completes and the whole sight warms up. */
.reticle.loaded .ring {
  stroke: #e8722c;
}

.reticle.loaded .pip {
  fill: #e8722c;
}

.reticle.loaded line {
  stroke: rgba(255, 255, 255, 0.8);
}

/* Charging a capacitor gun — the one case where holding the trigger matters. */
.reticle.charging .ring {
  stroke: #ffd166;
}

.setting-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.55rem;
}

.choices {
  display: flex;
  gap: 1px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.25rem;
  overflow: hidden;
}

.choices button {
  padding: 0.16rem 0.45rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  font-size: 0.68rem;
  letter-spacing: 0.03em;
}

.choices button.chosen {
  background: rgba(232, 114, 44, 0.85);
  color: #14171c;
  font-weight: 600;
}

/* ─── Taking over a tank ───────────────────────────────────────────────── */

.took-over {
  position: absolute;
  left: 50%;
  top: 22%;
  transform: translateX(-50%);
  padding: 0.4rem 0.9rem;
  border-radius: 0.2rem;
  background: rgba(12, 14, 18, 0.6);
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  pointer-events: none;
}

.took-over strong {
  color: #E8722C;
}

/* ─── Spawn-zone warning ──────────────────────────────────────────────── */

/*
 * "You are in the enemy spawn — move out!" Amber, the `.round-alert`
 * treatment: a warning about the match (here, about position) rather than a
 * result, which already has its own colours elsewhere on this HUD.
 *
 * Anchored by its own *bottom* edge rather than centred on the viewport, so
 * its own height can never creep down toward the sight: `.reticle` is a
 * 5.5rem ring dead centre, `2.75rem` is its own half-height, and this sits a
 * further, viewport-scaled gap above that — generous on a tall desktop
 * window, still clear of the ring on the shortest landscape phone this HUD
 * supports.
 */
/*
 * Big, central and mostly see-through — the one HUD element that has to be read
 * without being looked for, because it is the only warning in the game a player
 * can act on before anything has happened to them.
 *
 * It sits just under the top-centre round score rather than dead centre, so it
 * never covers the reticle: this is a message telling you to drive, and hiding
 * the aim point while you do would trade one problem for another. Wide enough to
 * be unmissable at a glance, at 22% background so the battlefield reads straight
 * through it, and `z-index` above the rest of the HUD because it is the only
 * thing worth reading while it is up.
 */
.spawn-zone-warning {
  position: absolute;
  left: 50%;
  top: clamp(4.5rem, 14vh, 8rem);
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: min(26rem, 74vw);
  padding: clamp(0.6rem, 2vh, 1.1rem) clamp(1.2rem, 5vw, 2.75rem);
  border: 2px solid rgba(232, 114, 44, 0.6);
  border-radius: 0.35rem;
  background: rgba(12, 14, 18, 0.22);
  backdrop-filter: blur(1px);
  text-align: center;
  pointer-events: none;
  animation: spawn-zone-warning-in 0.18s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

/* Once health is actually falling the box stops being an advisory. */
.spawn-zone-warning.damaging {
  border-color: rgba(226, 74, 58, 0.85);
  background: rgba(60, 12, 10, 0.3);
  animation: spawn-zone-warning-in 0.18s cubic-bezier(0.2, 0.7, 0.3, 1) both,
    spawn-zone-warning-pulse 0.8s ease-in-out infinite;
}

@keyframes spawn-zone-warning-pulse {
  50% { border-color: rgba(226, 74, 58, 0.35); }
}

/*
 * The animations are the *only* thing here that a reduced-motion preference
 * takes away. The box, its size and its colour all stay — this is a warning
 * about imminent damage, not decoration, and suppressing it would be the one
 * accessibility win that costs a player the tank.
 */
@media (prefers-reduced-motion: reduce) {
  .spawn-zone-warning,
  .spawn-zone-warning.damaging { animation: none; }
}

.spawn-zone-warning-text {
  font-size: clamp(1.05rem, 3.4vw, 1.9rem);
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.9);
}
.spawn-zone-warning.damaging .spawn-zone-warning-text { color: #ff8a7a; }

.spawn-zone-warning-count {
  font-size: clamp(2.2rem, 7vw, 4rem);
  line-height: 1;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.9);
}
/* The word replacing the number, so it must not be set at the number's size. */
.spawn-zone-warning-count.damaging {
  font-size: clamp(0.95rem, 2.8vw, 1.5rem);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ff8a7a;
}

@keyframes spawn-zone-warning-in {
  from {
    opacity: 0;
    transform: translate(-50%, 6px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spawn-zone-warning {
    animation: none;
  }
}

.destroyed-title.won {
  color: #4CAF6D;
}

/* ─── Standing orders ──────────────────────────────────────────────────── */

.orders {
  position: absolute;
  left: 1rem;
  bottom: 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.45rem 0.6rem;
  border-left: 2px solid rgba(232, 114, 44, 0.85);
  border-radius: 0.2rem;
  background: rgba(12, 14, 18, 0.55);
  pointer-events: none;
}

/* Every order is listed, so the *unchosen* ones are the quiet default and the
   standing one is what stands out. The inactive rows are a legend; they have to
   be readable enough to learn from and dim enough not to compete with the sight. */
.orders-line {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  white-space: nowrap;
  opacity: 0.45;
}

.orders-line.subtle {
  font-size: 0.7rem;
}

/* Density is a separate question from the order, so it gets a rule rather than
   just a gap — six rows in one column otherwise read as one list of six. */
.orders-line:not(.subtle) + .orders-line.subtle {
  margin-top: 0.4rem;
  padding-top: 0.4rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

/* The key cap. Monospaced so 1 and 4 occupy the same width and the two lines
   stay aligned however the labels change length. */
.orders-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.05rem;
  padding: 0.05rem 0;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 0.18rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.66rem;
  opacity: 0.85;
}

/* The standing order. Colour carries it rather than size, so the panel does not
   reflow every time the player presses a key — a legend that jumped about while
   you were reading it would be its own problem. */
.orders-line.active {
  opacity: 1;
}

.orders-line.active .orders-label {
  color: #E8722C;
  font-weight: 600;
}

.orders-line.active .orders-key {
  border-color: rgba(232, 114, 44, 0.85);
  opacity: 1;
}

/* ─── Round score ──────────────────────────────────────────────────────── */

/*
 * Top centre. The gunsight owns the middle of the screen and the corners are
 * taken by the roster, the speedometer and the orders panel, so the strip along
 * the top edge is what is left — and a score is something you glance at rather
 * than read.
 */
.rounds {
  position: absolute;
  top: 0.55rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.05rem;
  padding: 0.25rem 0.9rem;
  border-radius: 0.2rem;
  background: rgba(12, 14, 18, 0.55);
  pointer-events: none;
}

.rounds-score {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.rounds-sub {
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/* One round from the end, either way — worth saying out loud rather than
   leaving a player to do the arithmetic mid-fight. */
.rounds.match-point .rounds-sub {
  color: #E8722C;
}

/* ─── Mission strip ────────────────────────────────────────────────────── */

/*
 * The round score's slot, taken by the mission instead. Narrow on purpose and
 * hanging from the top edge: the middle of the screen is the gunsight and
 * everything the player is aiming at, which is the same constraint the tutorial
 * panel is shaped around.
 *
 * `min-width` rather than a fixed one, so "12 enemies remaining" and "Next wave
 * in 0:09" do not resize the strip under the number they are describing — a
 * readout that changes width every second reads as twitching rather than as
 * progress, which is the same reason the tutorial's counts are their own column.
 */
.mission {
  position: absolute;
  top: 0.55rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  min-width: 13rem;
  padding: 0.3rem 0.9rem 0.4rem;
  border-radius: 0.2rem;
  background: rgba(12, 14, 18, 0.62);
  pointer-events: none;
}

.mission-headline {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
}

.mission-sub {
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-400);
  font-variant-numeric: tabular-nums;
}

/* The one line that is meant to interrupt: a halted convoy, or one that has
   burned. It takes the accent rather than red, which is the enemy's colour and
   is already saying something else on this screen. */
.mission-alarm {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #e8722c;
}

.mission-sub em {
  font-style: normal;
  font-weight: 600;
  color: var(--steel-200, #d7dde3);
}

/* Where the core is, in one glance: ours, theirs, or on the ground. */
.mission-sub em.flag-carried {
  color: #4fe06a;
}

.mission-sub em.flag-dropped {
  color: #e8722c;
}

/* The convoy bar. The same construction as the health bar along the bottom —
   a track, a filled child and centred text — rather than a second bar idiom,
   because the two are read the same way and one of them is already tuned. */
.mission-bar {
  position: relative;
  display: block;
  width: 100%;
  height: 0.85rem;
  margin-top: 0.1rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.mission-bar i {
  display: block;
  height: 100%;
}

.mission-bar-text {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 0.58rem;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

/* ─── Airdrop buffs ────────────────────────────────────────────────────── */

.buffs {
  position: absolute;
  left: 50%;
  bottom: 7.5rem;
  transform: translateX(-50%);
  display: flex;
  gap: 0.4rem;
}

.buff {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid;
  border-radius: 0.25rem;
  background: rgba(12, 14, 18, 0.82);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
}

.buff-name {
  text-transform: uppercase;
  font-weight: 600;
}

.buff-time {
  color: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Compact: the battle HUD on a touch build, in landscape.
 *
 * ── Why a mode and not breakpoints ─────────────────────────────────────────
 *
 * Gated on `hud.touch` being present rather than on a width, because the
 * trigger is *are there thumbs in the bottom corners*, not *is the window
 * narrow*. A 1280×800 desktop window and a 1280×800 tablet want different HUDs
 * and a media query cannot tell them apart; meanwhile a phone in landscape is
 * 844×390, and a breakpoint there would also catch a small desktop window and
 * take a desktop player's scoreboard away. One structural condition, one mode.
 *
 * The tablet is included on purpose even though it has the height to spare: the
 * fire-circle-on-radar collision is 12,544px² at 1180×820, so the radar has to
 * move there too, and one mode is one thing to test.
 *
 * ── Why bands rather than corners ──────────────────────────────────────────
 *
 * Measured at 667×375, the smallest landscape phone: the HUD covered **60% of
 * the screen**, the scoreboard alone was 70% of the *height*, and the clear
 * middle band was **negative** — the roster reached y=280 while the thumbs
 * started at y=241. Height is the entire budget, so the screen is three
 * horizontal bands: a ~56px top row, a clear middle for the sight and the
 * nameplates, and a bottom band the thumbs own with the health bar between them.
 * ═══════════════════════════════════════════════════════════════════════════ */

/*
 * The collapsed score. Its own element rather than a restyled `.roster`, because
 * it shows a different fact — two totals instead of eight rows.
 */
.hud.compact .score-pill {
  position: absolute;
  top: 0.6rem;
  left: 0.6rem;
  /* 44px is the tap-target floor `CLAUDE.md` sets project-wide, and this is a
     control rather than a readout. */
  min-height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 0.3rem;
  background: rgba(10, 14, 18, 0.55);
  color: var(--steel-100, #eef2f6);
  font-family: var(--font-body);
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  pointer-events: auto;
}

/* The nameplate's own two colours, so a side means one thing everywhere. */
.hud.compact .score-pill-us {
  color: #4fe06a;
}

.hud.compact .score-pill-them {
  color: #d9483b;
}

.hud.compact .score-pill-dash {
  opacity: 0.45;
  font-weight: 400;
}

/*
 * The full roster: hidden, then an overlay.
 *
 * `display: none` rather than moved off-screen, so its eight rows of nameplates
 * and title badges are not laid out at all on the frames nobody is looking at
 * them — `publishHud` already runs per rendered frame and this is the largest
 * subtree it feeds.
 */
.hud.compact .roster {
  display: none;
}

.hud.compact .roster.open {
  display: flex;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
  max-height: calc(100vh - 1.6rem);
  overflow-y: auto;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.35rem;
  background: rgba(8, 11, 15, 0.92);
  z-index: 4;
  pointer-events: auto;
}

.hud.compact .roster-scrim {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: rgba(4, 6, 9, 0.5);
  pointer-events: auto;
}

/*
 * The top row: speed and the two buttons, tucked left of the radar.
 *
 * `right` clears the 104px scope plus its own 0.6rem inset plus a gutter. Stated
 * as a `calc` off the same numbers `RadarScope`'s compact rule uses, because the
 * two have to move together — a radar that grew would otherwise slide under this
 * row rather than pushing it aside.
 */
.hud.compact .readout {
  top: 0.6rem;
  right: calc(104px + 1.4rem);
  gap: 0.5rem;
  align-items: center;
}

/* The compact HUD shrinks the speed where it now lives, in the tally row. */
.hud.compact .tally-speed strong {
  font-size: 1.1rem;
}

/* Both are controls, so both take the tap-target floor. */
.hud.compact .readout button {
  min-height: 44px;
  padding-inline: 0.7rem;
}

/*
 * The settings panel opens under the row rather than beside it, and is capped so
 * it cannot run off a 375px-tall screen.
 */
.hud.compact .settings {
  top: 3.6rem;
  right: 0.6rem;
  width: min(17rem, calc(100vw - 1.2rem));
  max-height: calc(100vh - 4.4rem);
  overflow-y: auto;
}

/*
 * The kill feed hangs below the radar, and is capped at three lines.
 *
 * Unbounded it grew to four lines and 112px at 1180×820; on a 375px screen that
 * is a third of the height spent on events that have already happened.
 *
 * **Counted, not measured.** This was a `max-height: 3.4rem` for two lines, and
 * a height cannot express "three lines" when a line is not a fixed height: at
 * 13rem wide a long pair of names wraps onto two rows, so the same cap that
 * held two short lines clipped a single wrapped one — the reported bug. Hiding
 * the fourth-from-newest is exact whatever any line wraps to.
 *
 * `nth-last-child`, counting from the end, because `killFeed.push` puts the
 * newest line last. Counting from the start would hide the news and keep the
 * history.
 */
.hud.compact .kill-feed {
  top: calc(104px + 1.4rem);
  right: 0.6rem;
  max-width: 13rem;
}

.hud.compact .kill:nth-last-child(n + 4) {
  display: none;
}

/*
 * The health bar sits in the measured gap between the thumbs.
 *
 * `--thumb-gap` and `--thumb-gap-centre` come from the input source's own
 * layout, so this is the same pair of circles the hit-test uses — not an
 * approximation of them. The fallbacks matter for exactly one frame: the vars are
 * absent until the first HUD payload carries a touch view, and without them the
 * bar would be full width and under both thumbs on that frame.
 */
.hud.compact .status {
  left: var(--thumb-gap-centre, 50%);
  right: auto;
  transform: translateX(-50%);
  /*
   * A hair inside the gap rather than exactly it.
   *
   * At full gap width the bar's edge and the fire circle's edge land on the same
   * pixel and round into a 1px overlap — 35px² of a translucent bar under a
   * translucent ring, harmless in itself and exactly the kind of thing that turns
   * into a real overlap the moment either constant moves. Breathing room, not a
   * fix for a bug.
   */
  width: min(calc(var(--thumb-gap, 60vw) - 1rem), 16rem);
  bottom: 0.9rem;
  gap: 0.25rem;
}

/*
 * The bars themselves are cut to a fraction of the desktop height — compact
 * only, the desktop HUD keeps its 16px/10px pair. On 375px of screen the
 * desktop-sized bars were the tallest thing in the bottom band after the thumbs
 * themselves, and the width cap above (16rem, was 30rem) is half of the same
 * decision: between two thumbs the readout is the fill fraction and its colour,
 * not a panel.
 *
 * The text inside the bars goes with the height. Nothing legible fits a 4px
 * reload strip, and half-clipped digits read as a rendering bug rather than as
 * a small font; the health colour ramp and the reload's loaded-accent carry the
 * same two answers. The exact figures are still in the DOM (`.bar-text` is
 * display:none, not removed), so the harness and a screen reader keep them.
 */
.hud.compact .bar {
  height: 6px;
}

.hud.compact .bar.reload,
.hud.compact .bar.charge {
  height: 4px;
}

.hud.compact .bar-text {
  display: none;
}

.hud.compact .tally {
  font-size: 0.55rem;
}

/*
 * Buffs move up out of the thumb band.
 *
 * At `bottom: 7.5rem` they landed at y≈255 on a 375px screen, which is inside
 * the band the thumbs occupy from y=241. Above the health bar instead.
 */
.hud.compact .buffs {
  left: var(--thumb-gap-centre, 50%);
  bottom: calc(var(--thumb-band, 112px) + 0.6rem);
}

/*
 * The top-centre strips shrink rather than move: they are the one thing genuinely
 * in the middle, and the sight is below them.
 */
.hud.compact .rounds,
.hud.compact .mission {
  top: 0.4rem;
  font-size: 0.82rem;
}

/*
 * ─── The tutorial's panel, on a phone held sideways ──────────────────────────
 *
 * The other two things in this slot are *strips* — a score, a wave count — and
 * shrinking them was enough. This is a card with a title, prose, a goal list and
 * key caps in it, and at the desktop geometry it measured **416x185 of a 667x375
 * screen** centred at the top, which put it straight over the whole top-right
 * corner: the speed readout and the gear button, which on a touch build is the
 * *only* door to the escape menu and therefore the only way out of a mission
 * with no losing condition. The gear stayed tappable (this panel is
 * `pointer-events: none`) and became invisible, which is the worse half — a
 * control nobody can see is a control nobody presses.
 *
 * So on compact it stops being centred and takes the one band that is free: to
 * the right of the score pill, to the left of the readout row. Both edges are
 * stated as `calc`s off the same numbers those two rules use, because all three
 * have to move together — a radar that grew would push the readout left and this
 * would have to follow it.
 *
 * `left`/`right` with `width: auto` rather than a width and a translate, so the
 * clearance is the thing being expressed rather than a figure that happens to
 * produce it at 667px.
 */
.hud.compact .tutorial {
  top: 0.4rem;
  /*
   * Clear of the score pill, whose width is its content — a two-digit total each
   * side. Measured at 73px with the rig's own roster, so this is that plus the
   * pill's 0.6rem inset and a gutter, and `tutorial-controls` asserts the zero
   * rather than trusting the arithmetic. Every pixel to the left of this is a
   * word that does not have to wrap, so it is as tight as the measurement allows
   * — the pill measures 74px wide from x=10, and this clears its right edge by 10.
   */
  left: 5.9rem;
  /* Clear of the readout row, which is itself clear of the radar. The 7.9rem is
     that row's measured width — speed, gap, and the 44px gear. */
  right: calc(104px + 9.9rem);
  width: auto;
  /* A tablet has room for more than this panel needs, and a goal list set across
     30rem at this size reads as a spreadsheet. */
  max-width: 24rem;
  transform: none;
  padding: 0.45rem 0.6rem 0.4rem;
}

/*
 * Tighter, because the budget is height and this is the only panel on a phone
 * with four stacked parts to it. Every figure here is the desktop one cut, not a
 * new rhythm: the drills read the same, in less room.
 */
.hud.compact .tutorial-title {
  margin: 0.1rem 0 0.15rem;
  font-size: 0.92rem;
}

/*
 * ── The blurb goes, and this is the one cut worth arguing for ────────────────
 *
 * It is the only part of the panel that is *prose* — why the hull and the gun are
 * two controls, what the reload ring is — and dropping it hurts. What decided it
 * is arithmetic rather than taste: with everything above cut, the driving drill's
 * panel still measured **208px of a 375px screen**, so its bottom edge reached the
 * gunsight, which is the one thing the panel's own stylesheet says it must never
 * do. There is no third place for it to go: the top band is bounded by the score
 * pill, the readout and the radar, and everything below y=241 belongs to a thumb.
 *
 * It is also the least *lost* of the candidates, because the prose has a better
 * home on a phone than this panel ever was: the loading screen in front of the
 * tutorial now carries the whole touch control card — the stick, the drag, the
 * fire button, and where each one is on the screen — at a moment when the player
 * has nothing else to do and a reason to read. What stays here is the part that
 * changes per drill and has to be readable mid-fight: which drill, what to do,
 * which control, how far along.
 */
.hud.compact .tutorial-blurb {
  display: none;
}

.hud.compact .tutorial-goals {
  margin-top: 0.35rem;
}

.hud.compact .tutorial-hint {
  margin-top: 0.35rem;
}

/*
 * The caps come inline with the instruction, one row per goal.
 *
 * On the desktop panel they take a row of their own, for the reason stated on
 * `.tutorial-goal`: a 1.35rem cap beside a 0.76rem line sets the row height and
 * the ticks stop lining up with the labels they belong to. That is a *baseline*
 * problem, and it is solved here by centring the row instead — which the desktop
 * panel cannot do without moving its own ticks. The saving is ~16px per goal, and
 * the driving drill has two of them.
 */
.hud.compact .tutorial-goal {
  grid-template-columns: 1rem minmax(0, 1fr) auto auto;
  grid-template-areas: 'tick label keys count';
  align-items: center;
  gap: 0.3rem;
}

.hud.compact .tutorial-keys {
  margin-top: 0;
}

/*
 * And the caps themselves come down with everything else, which is the one cut
 * that needs a word: they are the brightest thing in the panel and the part a
 * player is actually looking for. 1.25rem is still 20px, and it owes nothing to
 * the 44px tap floor — a cap is a *label*, and the thing it names is a 112px ring
 * in the corner of the screen.
 */
.hud.compact .tutorial-keys .keycap {
  height: 1.25rem;
  font-size: 0.62rem;
}

/*
 * Nameplates are deliberately left alone.
 *
 * They are projected from world space and land anywhere, including under a thumb.
 * That was tempting to clip — and clipping is the wrong fix: a plate is already
 * dropped when its tank is behind the camera, so the ones that reach a thumb are
 * tanks the player is looking at, and hiding their names in the bottom band would
 * take information away exactly when it is wanted. A label partly behind a
 * translucent ring is still readable; the sight is the thing that had to be kept
 * clear, and the bands do that.
 */

/*
 * The menu button: one 44px icon where two text buttons were.
 *
 * Sized to the tap-target floor and no bigger, because the top row's whole
 * problem is width — at 667px it has to fit the speed, stay clear of the centre
 * strip and stop short of the radar, which leaves about 150px.
 */
.hud.compact .menu-button {
  display: grid;
  place-items: center;
  width: 44px;
  min-width: 44px;
  height: 44px;
  padding: 0;
}

/*
 * The size is set here rather than in `GearIcon`, which sets none: the garage bar
 * wants 1.45rem and this wants 20px inside a 44px target, so the caller decides.
 * `:deep` because the drawing is a child component's root.
 */
.hud.compact .menu-button :deep(.gear-icon) {
  width: 20px;
  height: 20px;
}

/* Turned when the menu is open, the same acknowledgement the garage's gear gives. */
.hud.compact .menu-button.active :deep(.gear-icon) {
  transform: rotate(30deg);
}

/*
 * ─── The result screen, on a phone held sideways ─────────────────────────────
 *
 * The debrief has to fit **one screen** on a 375px-tall landscape phone — the
 * fluid `vh` clamps above get the column from 895px down to ~700, and the rest
 * of the budget comes from `BattleSummary.vue`'s own compact recomposition
 * (keyed off the same `hud.touch` this class is). What lives here is only the
 * banner's and the column's share of it: the tightest rhythm the clamps allow,
 * a headline whose tracking is cut so MISSION COMPLETE still fits 667px beside
 * its rules, and a Return button back up at the 44px tap floor the clamps
 * would otherwise take it under.
 */
/*
 * On a phone the debrief scrolls rather than squeezing into one screen: the
 * column drops its height cap, and its foot keeps room for the pinned Return
 * button below so that, scrolled to the end, the button sits under the last
 * row rather than over it.
 */
.hud.compact .result-col {
  gap: 0.45rem;
  padding: 0.4rem 0 calc(4.5rem + env(safe-area-inset-bottom, 0px));
  max-height: none;
}

/*
 * The footer — Return to garage and the fuel button — pinned to the foot of the
 * screen, over a soft shade of its own rather than a band across the width:
 * enough to lift the buttons off rows scrolling beneath, not a second surface.
 * Centred with auto margins, not a transform, because the entrance animation
 * owns `transform`.
 */
.hud.compact .result-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: max(0.4rem, env(safe-area-inset-bottom, 0px));
  z-index: 4;
  width: max-content;
  margin-inline: auto;
  padding: 0.6rem 1.6rem;
  background: radial-gradient(closest-side, rgba(6, 8, 11, 0.6), rgba(6, 8, 11, 0.35) 60%, transparent);
}

.hud.compact .result-title {
  /* The height cap goes a step under the fluid rule's 8.5vh: a 375px screen
     draws the word at ~25px, which still carries a banner, where the 8px it
     gives back is a whole scoreboard row. */
  font-size: min(clamp(2.6rem, 7.5vw, 5.4rem), max(1.4rem, 6.8vh));
  letter-spacing: 0.18em;
  gap: 0.9rem;
}

.hud.compact .result-title-row {
  gap: 0.5rem;
}

.hud.compact .result-title-score {
  font-size: min(clamp(1.1rem, 4.2vw, 1.9rem), max(0.95rem, 5.2vh));
}

.hud.compact .result-head {
  gap: 0.15rem;
}

.hud.compact .result-word {
  margin-right: -0.18em;
}

.hud.compact .result-eyebrow {
  font-size: 0.58rem;
}

.hud.compact .result-sub {
  font-size: 0.64rem;
}

.hud.compact .result-score-unit {
  font-size: 0.55rem;
}

/* The one control on the screen: 44px on touch, whatever the clamps say. */
.hud.compact .result-leave {
  min-height: 44px;
  padding-block: 0.3rem;
  background-color: #2a1a10;
}
</style>